<template>
  <div class="tpd-wrapper">
    <!-- Header -->
    <div class="tpd-header">
      <div class="tpd-controls-grid">
        <div class="tpd-search">
          <input
            v-model.trim="manualArtist"
            placeholder="Search artist (e.g., Taylor Swift)"
            class="tpd-input"
            @keyup.enter="startResearch"
            aria-label="Search artist"
          />
          <button class="tpd-btn" @click="startResearch">Research</button>
        </div>

        <transition name="fade">
          <div class="tpd-thumb" v-if="artistThumb">
            <img :src="artistThumb" alt="Selected artist" />
          </div>
        </transition>
      </div>
<div aria-hidden="true" style="height:0;width:0;overflow:hidden;">
  <audio ref="cardOpen"     preload="auto" :src="sfxCardOpenUrl"></audio>
  <audio ref="cardComplete" preload="auto" :src="sfxCardCompleteUrl"></audio>
  <audio ref="xpTick"       preload="auto" :src="sfxXpTickUrl"></audio>
</div>
      <!-- GAMIFY HUD -->
      <section class="tpd-section" style="margin-top:8px;">
        <div class="hud neo-card" style="padding:10px; display:grid; grid-template-columns:1fr auto; gap:10px; align-items:center;">
          <!-- Left: Score + KPIs -->
          <div class="hud-metrics" style="display:flex; gap:14px; align-items:center; flex-wrap:wrap;">
            <div class="hud-pill pop-in">
              <span class="hud-label">Score</span>
              <span class="hud-value">{{ gamify.score }}</span>
            </div>
            <div class="hud-pill">
              <span class="hud-label">Level</span>
              <span class="hud-value">Lv {{ gamify.level }}</span>
            </div>
            <div class="hud-pill">
              <span class="hud-label">Searches</span>
              <span class="hud-value">{{ gamify.searchesSubmitted }}</span>
            </div>
            <div class="hud-pill">
              <span class="hud-label">Knowledge</span>
              <span class="hud-value">{{ gamify.knowledgeReceived }}</span>
            </div>
            <div class="hud-pill" title="Saved to Archive">
              <span class="hud-label">Archive</span>
              <span class="hud-value">{{ gamify.archivedCount }}</span>
            </div>
            <div class="hud-pill" title="Saved to Neural Brain">
              <span class="hud-label">Neural</span>
              <span class="hud-value">{{ gamify.neuralCount }}</span>
            </div>
          </div>

          <!-- Right: Recent badges (compact ribbon) -->
          <div class="hud-badges" style="display:flex; gap:6px; align-items:center; flex-wrap:wrap;">
            <transition-group name="pop" tag="div" style="display:flex; gap:6px; flex-wrap:wrap;">
              <span
                v-for="b in recentBadges"
                :key="b.id + '-' + b.ts"
                class="badge-chip"
                :title="b.label + ' — ' + formatDate(b.ts)"
              >
                {{ b.icon }} <small>{{ b.short }}</small>
              </span>
            </transition-group>
            <button class="tpd-btn tpd-btn-ghost" @click="hudExpanded = !hudExpanded" aria-label="Toggle activity feed">
              {{ hudExpanded ? 'Hide' : 'Activity' }}
            </button>
          </div>
        </div>

        <!-- Collapsible activity feed -->
        <transition name="fade">
          <div v-if="hudExpanded" class="neo-card" style="margin-top:8px; padding:12px;">
            <h4 style="margin:0 0 6px;">Recent Activity</h4>
            <ul class="hud-feed">
              <li v-for="(a,i) in activityFeed.slice(0,8)" :key="i" class="feed-item">
                <span class="feed-icon">{{ a.icon }}</span>
                <span class="feed-text">{{ a.text }}</span>
                <span class="feed-time dim">{{ timeago(a.ts) }}</span>
              </li>
            </ul>
          </div>
        </transition>
      </section>
    </div>

    <!-- Overlay while researching -->
    <div v-if="aiLoading" class="tpd-overlay" role="alert" aria-live="polite">
      <div class="tpd-overlay-card">
        <h3>Researching {{ manualArtist || selectedArtist || 'artist' }}…</h3>
        <img v-if="artistThumb" :src="artistThumb" alt="Artist" class="tpd-overlay-thumb" />
        <ul class="tpd-steps">
          <li v-for="(s, idx) in researchSteps" :key="s.label" :class="['tpd-step', s.status]">
            <span class="tpd-step-index">{{ idx + 1 }}</span>
            <span class="tpd-step-label">{{ s.label }}</span>
          </li>
        </ul>
        <div class="tpd-progress" aria-label="Research progress">
          <div class="tpd-progress-bar" :class="progressPhaseClass" :style="{ width: scanProgress + '%' }"></div>
        </div>
        <div class="tpd-status">{{ scanStatusText }}</div>
      </div>
    </div>

    <!-- KPI Deck -->
    <section class="tpd-section">
      <div v-if="kpisLoading" class="kpi-grid">
        <div class="neo-card kpi skeleton" v-for="n in 5" :key="n"></div>
      </div>

      <div v-else-if="kpis && kpis.totalShows != null" class="kpi-grid">
        <div class="neo-card kpi">
          <div v-if="forecast && forecast.artist" class="tpd-artist">
            <img :src="artistImg || placeholderImg" alt="Artist" class="tpd-artist-img" />
            <div class="tpd-artist-meta">
              <h4>{{ forecast.artist }}</h4>
              <p class="tpd-pattern" v-if="analysisSummary">{{ analysisSummary }}</p>
              <div class="tpd-actions">
                <button class="tpd-btn" @click="saveForecast">Save</button>
                <button class="tpd-btn tpd-btn-ghost" @click="deleteForecast(forecast.artist)">Delete</button>
                <button class="tpd-btn tpd-btn-ghost" @click="sendToArchive">Archive</button>
                <button class="tpd-btn tpd-btn-ghost" @click="sendToNeural">Neural Brain</button>
              </div>
            </div>
          </div>
        </div>

        <div class="neo-card kpi">
          <div class="kpi-icon">🎤</div>
          <div class="kpi-num">{{ kpis.totalShows.toLocaleString() }}</div>
          <div class="kpi-label">Total Past Shows</div>
          <br />
          <div class="kpi-icon">📅</div>
          <div class="kpi-num">{{ kpis.activeYears.toLocaleString() }}</div>
          <div class="kpi-label">Active Years</div>
        </div>

        <div class="neo-card kpi">
          <div class="kpi-icon">🌆</div>
          <div class="kpi-num">{{ kpis.uniqueCities.toLocaleString() }}</div>
          <div class="kpi-label">Unique Cities</div>
          <br />
          <div class="kpi-icon">🌍</div>
          <div class="kpi-num">{{ kpis.uniqueCountries.toLocaleString() }}</div>
          <div class="kpi-label">Countries</div>
        </div>

        <div class="neo-card kpi" v-if="kpis.avgCapacity">
          <div class="kpi-icon">🏟️</div>
          <div class="kpi-num">{{ Math.round(kpis.avgCapacity).toLocaleString() }}</div>
          <div class="kpi-label">Avg Capacity</div>
        </div>
      </div>

      <p v-else class="dim">No KPI data yet — run a search.</p>
    </section>

    <!-- ========= STAGED REVEAL SECTIONS ========= -->
    <transition-group name="reveal" tag="div" class="reveal-stack">
      <!-- Profile / Audience / Socials -->
<section
  v-if="reveal.profile && reportObj"
  key="profile"
  class="tpd-section card"
  :class="{ 'is-filling': filling.profile, 'is-complete': completed.profile }"
>
        <div class="grid" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:16px;">
          <!-- Profile -->
          <div class="neo-card" style="padding:14px;">
            <h3>Profile</h3>
            <ul class="dim" style="margin:8px 0 0; padding-left:18px;">
              <li v-if="reportObj.profile?.legalName"><strong>Legal:</strong> {{ reportObj.profile.legalName }}</li>
              <li v-if="(reportObj.profile?.aliases||[]).length"><strong>Aliases:</strong> {{ reportObj.profile.aliases.join(', ') }}</li>
              <li v-if="reportObj.profile?.origin"><strong>Origin:</strong> {{ reportObj.profile.origin }}</li>
              <li v-if="reportObj.profile?.label"><strong>Label:</strong> {{ reportObj.profile.label }}</li>
              <li v-if="reportObj.profile?.management"><strong>Management:</strong> {{ reportObj.profile.management }}</li>
              <li v-if="reportObj.profile?.officialWebsite">
                <strong>Site:</strong>
                <a :href="reportObj.profile.officialWebsite" target="_blank" rel="noopener">Official</a>
              </li>
            </ul>
            <p v-if="reportObj.profile?.bio" style="margin-top:10px;">{{ reportObj.profile.bio }}</p>
          </div>

          <!-- Audience -->
          <div class="neo-card" style="padding:14px;">
            <h3>Audience</h3>
            <ul class="dim" style="margin:8px 0 0; padding-left:18px;">
              <li v-if="artistData?.followers?.total != null"><strong>Spotify Followers:</strong> {{ artistData.followers.total.toLocaleString() }}</li>
              <li v-if="artistData?.popularity != null"><strong>Spotify Popularity:</strong> {{ artistData.popularity }}/100</li>
            </ul>
            <div v-if="(reportObj.profile?.awards||[]).length" style="margin-top:8px;">
              <br />
              <strong>Awards:</strong>
              <ul class="dim" style="margin:6px 0 0; padding-left:18px;">
                <li v-for="(a,i) in reportObj.profile.awards" :key="i">{{ a }}</li>
              </ul>
            </div>
          </div>

          <!-- Socials -->
          <div class="neo-card" style="padding:14px;">
            <h3>Socials</h3>
            <transition-group name="pop" tag="ul" class="tpd-socials">
              <li v-for="soc in normalizedSocials" :key="soc.key" class="tpd-social-item">
                <img class="tpd-social-icon" :src="soc.icon" :alt="soc.platform + ' icon'" width="28" height="28" />
                <div class="tpd-social-meta">
                  <strong class="tpd-social-name">{{ soc.platform }}</strong>
                  <small v-if="soc.handle" class="dim">@{{ soc.handle }}</small>
                </div>
                <a class="tpd-btn tpd-social-btn" :href="soc.url" target="_blank" rel="noopener" :aria-label="`Open ${soc.platform}`">
                  Open
                </a>
              </li>
            </transition-group>
            <p v-if="!normalizedSocials.length" class="dim" style="margin-top:8px;">
              No socials available.
            </p>
          </div>
        </div>
      </section>

      <!-- Contacts -->
      <section v-if="reveal.contacts && (reportObj?.contacts?.length)" key="contacts" class="tpd-section">
        <h3>Key Contacts</h3>
        <div class="grid" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:12px;">
          <div v-for="(c,i) in reportObj.contacts" :key="i" class="neo-card" style="padding:12px;">
            <strong>{{ c.role }}</strong>
            <div class="dim" style="margin-top:4px;">
              <span v-if="c.name">{{ c.name }}</span>
              <span v-if="c.company"> • {{ c.company }}</span>
            </div>
            <div class="dim" style="margin-top:4px;">
              <a v-if="c.website" :href="c.website" target="_blank" rel="noopener">Website</a>
              <span v-if="c.email"> · {{ c.email }}</span>
              <span v-if="c.phone"> · {{ c.phone }}</span>
              <a v-if="c.linkedin" :href="c.linkedin" target="_blank" rel="noopener"> · LinkedIn</a>
            </div>
            <div v-if="(c.socials||[]).length" class="dim" style="margin-top:4px;">Socials: {{ c.socials.join(', ') }}</div>
            <div v-if="c.notes" class="dim" style="margin-top:4px;">{{ c.notes }}</div>
          </div>
        </div>
      </section>

      <!-- Charts -->
      <section v-if="reveal.charts && (reportObj?.pastTours || []).length" key="charts" class="tpd-section">
        <div class="grid" style="display:grid;grid-template-columns:2fr 1fr;gap:14px;">
          <div class="neo-card" style="padding:12px;min-height:450px;">
            <h3 style="margin-bottom:8px;">Top Cities</h3>
            <div style="height:280px;">
              <BarChart :chartData="chartTopCities" />
            </div>
          </div>
          <div class="neo-card" style="padding:12px;min-height:450px;">
            <h3 style="margin-bottom:8px;">Countries</h3>
            <div style="height:280px;">
              <DoughnutChart :chartData="chartCountries" />
            </div>
          </div>
        </div>

        <div class="neo-card" style="padding:12px;margin-top:14px;">
          <h3 style="margin-bottom:8px;">Seasonality</h3>
          <div class="tpd-chart">
            <TourLinechart :chartData="chartSeasonality" @hoverYearIndex="handleHoverYear" />
          </div>
        </div>
      </section>
<br />
<br />
      <!-- Analysis (Artist Analysis + AI Executive Report) -->
      <section v-if="reveal.analysis && (analysisSummary || analysisDetails || reportObj?.analysis)" key="analysis" class="tpd-section">
        <div v-if="analysisSummary || analysisDetails" class="neo-card" style="padding:14px; margin-bottom:12px;">
          <h3 style="margin-bottom:8px;">Artist Analysis</h3>
          <p v-if="analysisSummary">{{ analysisSummary }}</p>
          <div v-if="analysisDetails" class="dim" v-html="analysisDetails"></div>
        </div>

        <div v-if="reportObj?.analysis" class="neo-card" style="padding:14px;">
          <h3 style="margin-bottom:8px;">AI Executive Report</h3>
          <p v-if="reportObj.analysis.bio"><strong>Bio:</strong> {{ reportObj.analysis.bio }}</p>
          <p v-if="reportObj.analysis.touring_patterns"><strong>Patterns:</strong> {{ reportObj.analysis.touring_patterns }}</p>

          <div v-if="reportObj.analysis.touring_team" style="margin-top:8px;">
            <strong>Touring Team</strong>
            <ul class="dim" style="margin:6px 0 0; padding-left:18px;">
              <li v-if="reportObj.analysis.touring_team.management">Management: {{ reportObj.analysis.touring_team.management }}</li>
              <li v-if="reportObj.analysis.touring_team.label">Label: {{ reportObj.analysis.touring_team.label }}</li>
              <li v-if="reportObj.analysis.touring_team.agent">Agent: {{ reportObj.analysis.touring_team.agent }}</li>
              <li v-if="reportObj.analysis.touring_team.promoter">Promoter: {{ reportObj.analysis.touring_team.promoter }}</li>
              <li v-if="reportObj.analysis.touring_team.notes">Notes: {{ reportObj.analysis.touring_team.notes }}</li>
            </ul>
          </div>

          <p v-if="reportObj.analysis.audience_summary" style="margin-top:8px;">
            <strong>Audience:</strong> {{ reportObj.analysis.audience_summary }}
          </p>

          <div v-if="reportObj.analysis.competitive_rank" style="margin-top:8px;">
            <strong>Competitive Rank:</strong>
            <span class="chip">{{ reportObj.analysis.competitive_rank.tier }}</span>
            <div class="dim" style="margin-top:4px;">{{ reportObj.analysis.competitive_rank.rationale }}</div>
          </div>

          <div class="grid" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:12px;margin-top:10px;">
            <div v-if="(reportObj.analysis.risks||[]).length" class="neo-card" style="padding:12px;">
              <strong>Risks</strong>
              <ul class="dim" style="margin:6px 0 0; padding-left:18px;">
                <li v-for="(r,i) in reportObj.analysis.risks" :key="i">{{ r }}</li>
              </ul>
            </div>
            <div v-if="(reportObj.analysis.opportunities||[]).length" class="neo-card" style="padding:12px;">
              <strong>Opportunities</strong>
              <ul class="dim" style="margin:6px 0 0; padding-left:18px;">
                <li v-for="(o,i) in reportObj.analysis.opportunities" :key="i">{{ o }}</li>
              </ul>
            </div>
          </div>

          <p v-if="reportObj.analysis.summary" style="margin-top:10px;"><strong>Summary:</strong> {{ reportObj.analysis.summary }}</p>
        </div>
      </section>

      <!-- Predictions -->
      <section v-if="reveal.predictions && (reportObj?.futureTours || []).length" key="pred" class="tpd-section">
        <h3>Predicted / Future Tours ({{ reportObj.futureTours.length }})</h3>
        <div class="neo-card" style="padding:0;">
          <div style="max-height:320px;overflow:auto;">
            <table style="width:100%; border-collapse:collapse;">
              <thead>
                <tr>
                  <th style="text-align:left;padding:10px;">Window</th>
                  <th style="text-align:left;padding:10px;">City</th>
                  <th style="text-align:left;padding:10px;">Venue</th>
                  <th style="text-align:left;padding:10px;">Region</th>
                  <th style="text-align:left;padding:10px;">Country</th>
                  <th style="text-align:right;padding:10px;">Capacity</th>
                  <th style="text-align:right;padding:10px;">Conf.</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(t,i) in reportObj.futureTours" :key="i" style="border-top:1px solid rgba(255,255,255,0.08);">
                  <td style="padding:10px;">{{ t.window || '—' }}</td>
                  <td style="padding:10px;">{{ t.city || '—' }}</td>
                  <td style="padding:10px;">{{ t.venue || '—' }}</td>
                  <td style="padding:10px;">{{ t.region || '—' }}</td>
                  <td style="padding:10px;">{{ t.country || '—' }}</td>
                  <td style="padding:10px; text-align:right;">{{ t.capacity != null ? t.capacity.toLocaleString() : '—' }}</td>
                  <td style="padding:10px; text-align:right;">{{ t.confidence != null ? t.confidence.toFixed(2) : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="(reportObj?.assumptions || []).length" class="neo-card" style="padding:14px; margin-top:12px;">
          <strong>Assumptions used by model</strong>
          <ul class="dim" style="margin:8px 0 0; padding-left:18px;">
            <li v-for="(a,i) in reportObj.assumptions" :key="i">{{ a }}</li>
          </ul>
        </div>
      </section>
    </transition-group>
    <!-- ========= END STAGED REVEAL ========= -->

    <!-- Past Tours Table (optional, outside staged reveal) -->
    <section v-if="(reportObj?.pastTours || []).length" class="tpd-section">
      <h3>Past Tours ({{ reportObj.pastTours.length }})</h3>
      <div class="neo-card" style="padding:0;">
        <div style="max-height:360px;overflow:auto;">
          <table style="width:100%; border-collapse:collapse;">
            <thead>
              <tr>
                <th style="text-align:left;padding:10px;">Date</th>
                <th style="text-align:left;padding:10px;">City</th>
                <th style="text-align:left;padding:10px;">Venue</th>
                <th style="text-align:left;padding:10px;">Region</th>
                <th style="text-align:left;padding:10px;">Country</th>
                <th style="text-align:right;padding:10px;">Capacity</th>
                <th style="text-align:left;padding:10px;">Source</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(t,i) in reportObj.pastTours" :key="i" style="border-top:1px solid rgba(255,255,255,0.08);">
                <td style="padding:10px;">{{ t.date || '—' }}</td>
                <td style="padding:10px;">{{ t.city || '—' }}</td>
                <td style="padding:10px;">{{ t.venue || '—' }}</td>
                <td style="padding:10px;">{{ t.region || '—' }}</td>
                <td style="padding:10px;">{{ t.country || '—' }}</td>
                <td style="padding:10px; text-align:right;">{{ t.capacity != null ? t.capacity.toLocaleString() : '—' }}</td>
                <td style="padding:10px;">
                  <a v-if="t.source && /^https?:/i.test(t.source)" :href="t.source" target="_blank" rel="noopener">link</a>
                  <span v-else>{{ t.source || '—' }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- Time series popup (outside reveal; pairs with TourLinechart hover) -->
    <section v-if="forecast && forecast.frequencyData" class="tpd-section">
      <h3>Tour Activity Over Time</h3>
      <div class="tpd-chart">
        <TourLinechart :chartData="forecast.frequencyData" @hoverYearIndex="handleHoverYear" />
      </div>
      <div v-if="popupActive" class="tpd-year-popup">
        <h4>Events in {{ selectedYear }}</h4>
        <ul>
          <li v-for="(ev, i) in selectedEvents" :key="i">
            <strong>{{ ev.date }}</strong> — {{ ev.city }} — {{ ev.venue }}
          </li>
        </ul>
      </div>
    </section>
<br />
<br />
    <!-- Map -->
    <section class="tpd-section" v-if="hasAnyTours">
      <div class="tpd-map-toolbar" style="display:flex;align-items:center;gap:12px;margin-bottom:8px;flex-wrap:wrap;">
        <h3 style="margin:0;">Tour Map</h3>
        <label style="display:flex;align-items:center;gap:6px;margin-left:auto;">
          <span class="dim">Show:</span>
          <select v-model="mapMode" @change="refreshMap" aria-label="Map dataset">
            <option value="past">Past Tours</option>
            <option value="predicted">Predicted Tours</option>
          </select>
        </label>
        <label v-if="mapMode==='past'" style="display:flex;align-items:center;gap:6px;">
          <span class="dim">Year:</span>
          <select v-model="mapYear" @change="refreshMap" aria-label="Past tours year filter">
            <option value="all">All</option>
            <option v-for="y in mapYears" :key="y" :value="y">{{ y }}</option>
          </select>
        </label>
        <span class="chip" v-if="hasMapPoints">{{ currentMapPoints.length }} points</span>
      </div>

      <div class="tpd-map-wrap" style="position:relative;">
        <div
          v-show="!gmapLoaded"
          class="tpd-map-skel dim"
          style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;z-index:1;"
        >
          Loading map…
        </div>

        <div
          ref="tourMap"
          class="tpd-map"
          :class="{ 'is-loading': !gmapLoaded }"
          aria-label="Tour map"
          role="img"
          aria-live="polite"
          style="min-height:380px;border-radius:12px;overflow:hidden;"
        ></div>
      </div>

      <div class="tpd-map-legend dim" style="margin-top:8px;display:flex;align-items:center;gap:14px;">
        <span style="display:inline-flex;align-items:center;gap:6px;">
          <span class="legend-dot" style="width:10px;height:10px;border-radius:50%;background:#7aa8ff;display:inline-block;"></span>
          Past
        </span>
        <span style="display:inline-flex;align-items:center;gap:6px;">
          <span class="legend-dot" style="width:10px;height:10px;border-radius:50%;background:#8ef0b4;display:inline-block;"></span>
          Predicted
        </span>
      </div>

      <p v-if="gmapLoaded && !hasMapPoints" class="dim" style="margin-top:8px;">No mappable shows yet.</p>
    </section>

    <!-- Sources -->
    <section v-if="normalizedSources.length" class="tpd-section">
      <h3>Sources</h3>
      <div class="neo-card" style="padding:14px;">
        <ul class="dim" style="margin:0; padding-left:18px;">
          <li v-for="(s,i) in normalizedSources" :key="i">
            <a v-if="s.url" :href="s.url" target="_blank" rel="noopener">{{ s.title || s.url }}</a>
            <span v-else>{{ s.title || 'Source' }}</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- Saved forecasts -->
    <v-divider dark />
    <section v-if="savedForecasts.length" class="tpd-section">
      <h3>Saved Forecasts</h3>
      <div class="tpd-saved">
        <div class="tpd-card" v-for="rec in savedForecasts" :key="rec.artist">
          <img :src="rec.artistImg || placeholderImg" alt="artist" />
          <div class="tpd-card-meta">
            <h4>{{ rec.artist }}</h4>
            <small>Saved: {{ formatDate(rec.savedAt) }}</small>
          </div>
          <div class="tpd-card-actions">
            <button class="tpd-btn" @click="loadForecast(rec.artist)">Open</button>
            <button class="tpd-btn tpd-btn-ghost" @click="deleteForecast(rec.artist)">Remove</button>
          </div>
        </div>
      </div>
    </section>

    <!-- Error -->
    <p v-if="errorMessage" class="tpd-error">{{ errorMessage }}</p>

    <!-- SFX (persistent refs) -->
    <!-- <div aria-hidden="true" style="height:0; width:0; overflow:hidden;">
      <audio ref="sfxPing"  preload="auto" src="https://cdn.jsdelivr.net/gh/terkelg/awesome-creative-coding-sfx/ping.mp3"></audio>
      <audio ref="sfxBadge" preload="auto" src="https://cdn.jsdelivr.net/gh/michalosman/chime-sfx/chime1.mp3"></audio>
      <audio ref="sfxPoint" preload="auto" src="https://cdn.jsdelivr.net/gh/naptha/tonejs-instruments@master/sounds/ui/click.mp3"></audio>
    </div> -->
  </div>
</template>


<script>
/* global google */
import axios from 'axios';
import { Line, Bar, Doughnut } from 'vue-chartjs';
import sfxCardOpenUrl     from '@/assets/audio/CogDing.wav';
import sfxCardCompleteUrl from '@/assets/audio/CogFuture.wav';
import sfxXpTickUrl       from '@/assets/audio/Complete.wav';
// ⬇️ Put decycle at top-level (module scope)
// helpers (put once in the file)
// ---- helpers (put once near the top of <script>) ----
const _root =
  (typeof window !== 'undefined' && window) ||
  (typeof self !== 'undefined' && self) ||
  (typeof global !== 'undefined' && global) ||
  {}; // fallback
// put once near your methods (or top of <script>)
const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const _hasStructuredClone = typeof _root.structuredClone === 'function';
const MAP_COLORS = {
  past: '#7aa8ff',       // blue
  predicted: '#8ef0b4'   // green
};
export const deepClone = (value) =>
  _hasStructuredClone
    ? _root.structuredClone(value)
    : JSON.parse(JSON.stringify(value)); // no circulars here; see decycle below

// JSON replacer that drops circular refs (safe for console/debug)
const decycle = () => {
  const seen = new WeakSet();
  return (key, value) => {
    if (value && typeof value === 'object') {
      if (seen.has(value)) return; // remove circular reference
      seen.add(value);
    }
    return value;
  };
};
// --- SFX Engine (single AudioContext + per-sound rate limits) ---
const SFX_MIN_GAP_MS = { cardOpen: 250, cardComplete: 350, xpTick: 140 };

function once(fn) {
  let ran = false;
  return (...args) => { if (ran) return; ran = true; return fn(...args); };
}
const unlockOnFirstGesture = once((ctx) => {
  const tryResume = () => { ctx.resume && ctx.resume(); window.removeEventListener('pointerdown', tryResume, true); window.removeEventListener('keydown', tryResume, true); };
  window.addEventListener('pointerdown', tryResume, true);
  window.addEventListener('keydown', tryResume, true);
});

// ---- CHART PALETTE & HELPERS ----
const PALETTE = Object.freeze({
  red:      { solid: '#EF4444', fill: 'rgba(239, 68, 68, 0.25)' },   // primary
  amber:    { solid: '#F59E0B', fill: 'rgba(245, 158, 11, 0.22)' },
  violet:   { solid: '#8B5CF6', fill: 'rgba(139, 92, 246, 0.22)' },
  cyan:     { solid: '#06B6D4', fill: 'rgba(6, 182, 212, 0.22)' },
  emerald:  { solid: '#10B981', fill: 'rgba(16, 185, 129, 0.22)' },
  rose:     { solid: '#F43F5E', fill: 'rgba(244, 63, 94, 0.22)' },
  blue:     { solid: '#3B82F6', fill: 'rgba(59, 130, 246, 0.22)' },
  slate:    { solid: '#94A3B8', fill: 'rgba(148, 163, 184, 0.20)' }
});

// Cycle across bold colors for multi-series / categories
function getOrdinalColors(n) {
  const order = ['red','amber','violet','cyan','emerald','rose','blue','slate'];
  const out = [];
  for (let i = 0; i < n; i++) {
    const k = order[i % order.length];
    out.push(PALETTE[k]);
  }
  return out;
}

// Ensure a dataset has visible colors; if it doesn't, inject them
function ensureBarDoughnutColors(chartData) {
  if (!chartData?.datasets?.length) return chartData;
  const colors = getOrdinalColors(chartData.datasets.length);
  chartData.datasets.forEach((ds, i) => {
    const c = colors[i];
    ds.backgroundColor = ds.backgroundColor || c.fill;
    ds.borderColor     = ds.borderColor     || c.solid;
    ds.borderWidth     = ds.borderWidth     || 2;
    ds.hoverBorderWidth = ds.hoverBorderWidth || 3;
  });
  return chartData;
}

// Ensure line styles (strong red primary, visible points)
function ensureLineColors(chartData) {
  if (!chartData?.datasets?.length) return chartData;
  const ds = chartData.datasets[0];
  const c = PALETTE.red;
  ds.borderColor        = ds.borderColor        || c.solid;
  ds.backgroundColor    = ds.backgroundColor    || c.fill;
  ds.pointBackgroundColor = ds.pointBackgroundColor || c.solid;
  ds.pointBorderColor     = ds.pointBorderColor     || '#0b1220';
  ds.borderWidth        = ds.borderWidth        || 4;
  ds.pointRadius        = ds.pointRadius        || 4;
  ds.pointHoverRadius   = ds.pointHoverRadius   || 7;
  ds.pointHitRadius     = ds.pointHitRadius     || 10;
  ds.lineTension        = ds.lineTension ?? 0.35; // chart.js v2
  return chartData;
}

// Common dark-UI axes + tooltip config (Chart.js v2 style)
const DARK_CHART_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 1200, easing: 'easeOutQuart' },
  hover: { animationDuration: 350 },
  tooltips: {
    backgroundColor: 'rgba(10, 20, 35, 0.95)',
    titleFontSize: 14,
    bodyFontSize: 13,
    xPadding: 10,
    yPadding: 10,
    displayColors: false
  },
  legend: {
    labels: {
      fontFamily: "'Orbitron', monospace",
      fontSize: 13,
      fontColor: '#E5F0FF'
    }
  },
  scales: {
    xAxes: [{
      gridLines: { color: 'rgba(255,255,255,0.12)', zeroLineColor: 'rgba(255,255,255,0.18)' },
      ticks: { fontColor: '#D6E6FF', fontSize: 12 }
    }],
    yAxes: [{
      gridLines: { color: 'rgba(255,255,255,0.12)', zeroLineColor: 'rgba(255,255,255,0.18)' },
      ticks: { fontColor: '#D6E6FF', fontSize: 12, beginAtZero: true, precision: 0 }
    }]
  }
};


// -------- BarChart --------
const BarChart = {
  name: 'BarChart',
  extends: Bar,
  props: { chartData: Object, chartOpts: Object },
  mounted() {
    if (!this.chartData) return;
    const data = deepClone(this.chartData);
    ensureBarDoughnutColors(data);
    this.renderChart(data, Object.assign({}, DARK_CHART_OPTS, this.chartOpts || {}));
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        if (!this.chartData) return;
        const data = deepClone(this.chartData);
        ensureBarDoughnutColors(data);
        this.renderChart(data, Object.assign({}, DARK_CHART_OPTS, this.chartOpts || {}));
      }
    }
  }
};

// -------- DoughnutChart --------
const DoughnutChart = {
  name: 'DoughnutChart',
  extends: Doughnut,
  props: { chartData: Object, chartOpts: Object },

  methods: {
    _withPrimaryRed(data) {
      const red = (typeof PALETTE !== 'undefined' && PALETTE?.red?.fill) ? PALETTE.red.fill : '#ff3344';
      const labelsLen = (data.labels || []).length;

      // Base palette for all slices
      const palette = getOrdinalColors(labelsLen).map(c => c.fill);
      if (palette.length) palette[0] = red;

      // Ensure dataset exists
      if (!data.datasets || !data.datasets[0]) {
        data.datasets = [{ data: [], backgroundColor: palette }];
      }

      // Respect an existing backgroundColor array but force index 0 to red
      const bg = data.datasets[0].backgroundColor;
      if (Array.isArray(bg) && bg.length) {
        const patched = bg.slice(0, labelsLen);
        if (patched.length < labelsLen) {
          // pad with palette if the incoming bg is shorter than labels
          for (let i = patched.length; i < labelsLen; i++) patched[i] = palette[i] || palette[i % palette.length];
        }
        patched[0] = red;
        data.datasets[0].backgroundColor = patched;
      } else {
        data.datasets[0].backgroundColor = palette;
      }

      // Borders & hover
      data.datasets[0].borderColor       = data.datasets[0].borderColor ?? '#0b1220';
      data.datasets[0].borderWidth       = data.datasets[0].borderWidth ?? 2;
      data.datasets[0].hoverBorderWidth  = data.datasets[0].hoverBorderWidth ?? 3;

      return data;
    },

    _opts() {
      return Object.assign({}, DARK_CHART_OPTS, {
        cutoutPercentage: 58,
        animation: { animateRotate: true, animateScale: true, duration: 1200, easing: 'easeOutQuart' },
        scales: undefined // doughnut has no axes
      }, this.chartOpts || {});
    }
  },

  mounted() {
    if (!this.chartData) return;
    const data = this._withPrimaryRed(deepClone(this.chartData));
    this.renderChart(data, this._opts());
  },

  watch: {
    chartData: {
      deep: true,
      handler() {
        if (!this.chartData) return;
        const data = this._withPrimaryRed(deepClone(this.chartData));
        this.renderChart(data, this._opts());
      }
    }
  }
};


// -------- Line (TourLinechart) --------
const TourLinechart = {
  name: 'TourLinechart',
  extends: Line,
  props: { chartData: { type: Object, default: () => ({}) } },
  data() { return { hoverIndex: null }; },
  mounted() { this.render(); },
  watch: { chartData: { deep: true, handler() { this.render(); } } },
  methods: {
    render() {
      if (!this.chartData || !this.chartData.labels) return;
      const data = deepClone(this.chartData);
      ensureLineColors(data);
      const opts = Object.assign({}, DARK_CHART_OPTS, {
        elements: {
          line: { tension: 0.35, borderWidth: 4 },
          point: { radius: 4, hoverRadius: 7, borderWidth: 2 }
        },
        tooltips: Object.assign({}, DARK_CHART_OPTS.tooltips, {
          callbacks: {
            title: items => items[0].label,
            label: item => `Shows: ${item.value}`
          }
        }),
        hover: {
          onHover: (e, items) => {
            this.hoverIndex = items.length ? items[0]._index : null;
            this.$emit('hoverYearIndex', this.hoverIndex);
          },
          animationDuration: 350
        }
      }, this.chartOpts || {});
      this.renderChart(data, opts);
    }
  }
};
// -------- API KEYS & HELPERS --------


const GOOGLE_MAPS_API_KEY   = 'AIzaSyBboVjZMYbpCulM_NhAuW5I_e6ZwD5f8qA'
const OPENAI_API_KEY        = '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'
const SPOTIFY_CLIENT_ID     = '1522ec4d96914840aef42817fe1b387b'
const SPOTIFY_CLIENT_SECRET = '673c7686457a438488401835249dca6b'
const TICKETMASTER_API_KEY  = 'urzDHfIpUlR6bHW3gSuErLG9Vt3yEdKm'
const SETLISTFM_API_KEY     = 'YOBeMYAiutbxYRjG2KX1bYmKx13uLCU2t72F'

const AI_TIMEOUT_MS = 25000;
const AI_PRIMARY_MODEL  = 'gpt-5';
const AI_FALLBACK_MODEL = 'gpt-4.1';

function buildChatBody({ model, system, user, maxTokens = 6000, temperature = 0.2 }) {
  return {
    model,
    temperature,
    max_tokens: maxTokens,
    response_format: { type: 'json_object' },
    messages: [
      { role: 'system', content: system || 'You output only JSON. Do not include commentary.' },
      { role: 'user',   content: user }
    ]
  };
}

async function callOpenAIChat(body, { retries = 1, timeoutMs = AI_TIMEOUT_MS } = {}) {  const url = 'https://api.openai.com/v1/chat/completions';
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${OPENAI_API_KEY}` };

  for (let attempt = 0; attempt <= retries; attempt++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res  = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body), signal: ctrl.signal });
      const data = await res.json().catch(() => ({}));
      clearTimeout(timer);
      if (!res.ok) {
        const msg = data?.error?.message || `HTTP ${res.status}`;
        if (/model .* not exist|Unknown model|unsupported model/i.test(msg) && body.model !== AI_FALLBACK_MODEL) {
          return await callOpenAIChat({ ...body, model: AI_FALLBACK_MODEL }, { retries: 0, timeoutMs });
        }
        throw new Error(msg);
      }
      return data;
    } catch (e) {
      clearTimeout(timer);
      if (attempt === retries) throw e;
      await new Promise(r => setTimeout(r, 500 + attempt * 400));
    }
  }
  throw new Error('OpenAI call failed');
}

function extractChatJson(data) {
  const text = data?.choices?.[0]?.message?.content ?? '';
  try { return JSON.parse(text); } catch { return null; }
}

window.__AI_SCHEMAS__ = Object.freeze({
  PAST_TOURS: 'shape: { pastTours: [{ city, venue, country, region, date, capacity, lat, lng, source }] }',
  PREDICTIONS: 'shape: { artist, assumptions[], predictions:[{ city, venue, country, region, window, confidence, capacity, lat, lng }] }',
  CONTACTS: 'shape: { contacts:[{ role, name, company, email, phone, website, linkedin, socials[], notes }] }',
  ARTIST_PROFILE: 'shape: { profile:{ legalName, aliases[], origin, label, management, bio, awards[], socials[], officialWebsite } }'
});


export default {
  name: 'TourPredictorDashboard',
  components: { TourLinechart, BarChart, DoughnutChart },

props: {
  reportObj: {
    type: Object,
    default: () => ({ contacts: [] })
  }
},

  data() {
    return {
      selectedArtist: '',
      manualArtist: this.artistName || '',
      artistImg: '',
      placeholderImg: 'https://via.placeholder.com/360x360?text=Artist',
      aiLoading: false,
      researchSteps: [
        { label: '🎧 Searching Artist', status: 'pending' },
        { label: '📷 Fetching Artist Image', status: 'pending' },
        { label: '🤖 Running AI Forecast', status: 'pending' },
        { label: '✅ Forecast Complete', status: 'pending' }
      ],
      scanProgress: 0,
      scanStatusText: 'Initializing…',
      forecast: null,
      aiLog: {},
      savedForecasts: [],
      currentContact: null,
      localReport: { contacts: [] },
      tourMap: null,
      map: null,
      gmap: null,
      gmapPolyline: null,
      gmapLoaded: false,
      selectedYear: null,
      selectedEvents: [],
      popupActive: false,
      kpis: null,
hudExpanded: false,
sfxCardOpenUrl,
    sfxCardCompleteUrl,
    sfxXpTickUrl,
gamify: {
  score: 0,
  level: 1,
  searchesSubmitted: 0,
  knowledgeReceived: 0,
  archivedCount: 0,
  neuralCount: 0,
  badges: [] // { id, label, short, icon, ts }
},
activityFeed: [], // { ts, icon, text }
sfx: { cardOpen: null, cardComplete: null, xpTick: null },
reveal: {
  profile: false,
  contacts: false,
  charts: false,
  analysis: false,
  predictions: false
},

    kpisLoading: false,
      spotlightArtists: [
        'Peso Pluma', 'Steve Lacy', 'Sabrina Carpenter', 'Metro Boomin', 'Noah Kahan',
        'Rema', 'Burna Boy', 'Ice Spice', 'Dua Lipa', 'Olivia Rodrigo', 'Måneskin', 'Billie Eilish', 'Taylor Swift', 'Beyoncé'
      ],
artistData: null,
pastConcerts: [],
futureTours: [],
tourStats: {},
mapLayer: null,
      mapCluster: null,
      mapMarkers: [],
      mapMode: 'past',     // 'past' | 'predicted'
      mapYear: 'all',      // 'all' or a specific year string
      lastBoundsFit: false,
mapDrawTimer: null,
mapRedrawTimer: null,   // renamed from _mapRedrawTimer
    gmMarkers: [],          // renamed from _gmMarkers
    gmAnimRAF: null,        // renamed from _gmAnimRAF
    gmapLoaderPromise: null,
    analysisSummary: '',
    analysisDetails: '',
      errorMessage: '',
      storageKey: 'tourPredictions',
sfxEnabled: true,
    sfxCtx: null,
    sfxBuffers: {},       // { cardOpen: AudioBuffer, cardComplete: AudioBuffer, xpTick: AudioBuffer }
    sfxLastAt: {},        // timestamps for min-gap gating
    tickLoop: null, 
  // per-card animation flags
  filling: { profile:false, contacts:false, charts:false, analysis:false, predictions:false },
  completed: { profile:false, contacts:false, charts:false, analysis:false, predictions:false },

  // HUD / gamify micro-anims
  hudPulse: false,
  xpBurst: false,


    };
  },

computed: {
spotlightArtistsClean() {
    const list = Array.isArray(this.spotlightArtists) ? this.spotlightArtists : [];
    const out = [];
    const seen = new Set();
    for (const s of list) {
      const v = String(s || '').trim();
      if (!v || seen.has(v.toLowerCase())) continue;
      seen.add(v.toLowerCase());
      out.push(v);
    }
    return out;
  },
    hasAnyTours() {
      const p = Array.isArray(this.reportObj?.pastTours) ? this.reportObj.pastTours : [];
      const q = Array.isArray(this.reportObj?.predictedTours) ? this.reportObj.predictedTours : [];
      return p.length > 0 || q.length > 0;
    },

    // Build year options from past tours with a parseable date
    mapYears() {
      const years = new Set();
      const list = Array.isArray(this.reportObj?.pastTours) ? this.reportObj.pastTours : [];
      list.forEach(t => {
        const y = this.extractYear(t?.date);
        if (y) years.add(y);
      });
      return Array.from(years).sort((a,b) => b - a); // desc
    },
    xpPct() {
  return Math.min(100, Math.round((this.gamify.xp / this.gamify.nextLevelXp) * 100));
},
    // Normalize Socials: accept string or object, dedupe by (platform + url)
normalizedSocials() {
    const raw = (this.reportObj?.profile?.socials || this.reportObj?.socials || []);
    const list = Array.isArray(raw) ? raw : [];

    const out = [];
    const seen = new Set();

    for (const s of list) {
      // Accept string URLs or objects like { platform, url, handle }
      const obj = (s && typeof s === 'object')
        ? { platform: s.platform || this._inferPlatform(s.url || s.link || ''), url: s.url || s.link || '', handle: s.handle || '' }
        : { platform: this._inferPlatform(String(s || '')), url: String(s || ''), handle: '' };

      if (!obj.url || !/^https?:\/\//i.test(obj.url)) continue;

      const key = `${obj.platform}|${obj.url}`.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);

      out.push({
        key,
        platform: this._prettyPlatform(obj.platform),
        url: obj.url,
        handle: obj.handle || this._inferHandle(obj.url),
        icon: this._socialIconDataUri(obj.platform)
      });
    }
    return out;
  },
    // Points currently visible by mode/year
    currentMapPoints() {
    // ✅ Use futureTours (your schema), not predictedTours
    const raw = this.mapMode === 'past'
      ? (Array.isArray(this.reportObj?.pastTours) ? this.reportObj.pastTours : [])
      : (Array.isArray(this.reportObj?.futureTours) ? this.reportObj.futureTours : []);

    // Filter by year (past only)
    const filtered = (this.mapMode === 'past' && this.mapYear !== 'all')
      ? raw.filter(t => this.extractYear(t?.date) === Number(this.mapYear))
      : raw;

// in computed currentMapPoints(), right after you map/normalize:
return filtered
  .map(t => {
    const p = this.normalizePoint(t);
    if (p) p.kind = (this.mapMode === 'past') ? 'past' : 'predicted';
    return p;
  })
  .filter(p => p && this.isFiniteLatLng(p.lat, p.lng));

  },

    hasMapPoints() {
      return Array.isArray(this.currentMapPoints) && this.currentMapPoints.length > 0;
    },
    mapPoints() { return this.currentMapPoints; },
    // Normalize Sources: accept string or object, dedupe by (url || title)
    normalizedSources() {
      const srcs = Array.isArray(this.reportObj?.sources) ? this.reportObj.sources : [];
      const out = [];
      const seen = new Set();
      srcs.forEach(s => {
        let obj = null;
        if (typeof s === 'string') {
          obj = { title: s, url: this.maybeUrl(s) };
        } else if (s && typeof s === 'object') {
          obj = {
            title: s.title || s.url || 'Source',
            url: this.maybeUrl(s.url)
          };
        }
        if (!obj) return;
        const key = (obj.url || obj.title || '').trim().toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        out.push(obj);
      });
      return out;
    },
    progressPhaseClass() {
    if (this.scanProgress >= 100) return 'phase-100';
    if (this.scanProgress >= 80)  return 'phase-80';
    if (this.scanProgress >= 60)  return 'phase-60';
    return 'phase-0';
  },
artistThumb() {
  // Prefer fetched image; fall back to placeholder when we have an artist context
  const hasContext = !!(this.forecast?.artist || this.manualArtist || this.selectedArtist);
  return (this.artistImg || (hasContext ? this.placeholderImg : '')) || '';
},

// kpis() {
//   const past = this.reportObj?.pastTours || [];
//   const cities = new Set(past.map(t => `${t.city||''}|${t.country||''}`));
//   const countries = new Set(past.map(t => t.country || '').filter(Boolean));
//   const years = new Set(past.map(t => String(t.date||t.window||'').slice(0,4)).filter(y => /^\d{4}$/.test(y)));

//   const avgCapacity = (() => {
//     const vals = past.map(t => Number(t.capacity)).filter(n => Number.isFinite(n));
//     if (!vals.length) return null;
//     return Math.round(vals.reduce((a,b)=>a+b,0) / vals.length);
//   })();

//   return {
//     totalShows: past.length,
//     uniqueCities: cities.size,
//     uniqueCountries: countries.size,
//     activeYears: years.size,
//     avgCapacity
//   };
// },
chartTopCities() {
  const past = this.reportObj?.pastTours || [];
  const count = {};
  for (const t of past) {
    const k = (t.city || '—') + (t.country ? `, ${t.country}` : '');
    count[k] = (count[k] || 0) + 1;
  }
  const entries = Object.entries(count).sort((a,b)=>b[1]-a[1]).slice(0,15);
  return {
    labels: entries.map(e => e[0]),
    datasets: [{
      label: 'Shows',
      data: entries.map(e => e[1]),
      // Optional—components will color if you don’t set these:
      backgroundColor: PALETTE.red.fill,
      borderColor: PALETTE.red.solid,
      borderWidth: 2
    }]
  };
},

chartCountries() {
  const past = this.reportObj?.pastTours || [];
  const count = {};
  for (const t of past) {
    const k = t.country || 'Unknown';
    count[k] = (count[k] || 0) + 1;
  }

  const labels = Object.keys(count);
  const data   = labels.map(k => count[k]);

  // Build dramatic palette: force the first category to red, others cycle
  const fills = getOrdinalColors(labels.length).map(c => c.fill);
  if (fills.length) fills[0] = PALETTE.red.fill;

  return {
    labels,
    datasets: [{
      data,
      backgroundColor: fills,
      borderColor: '#0b1220',
      borderWidth: 2,
      hoverBorderWidth: 3
    }]
  };
},


chartSeasonality() {
  const past = this.reportObj?.pastTours || [];
  const months = Array(12).fill(0);
  for (const t of past) {
    const ds = String(t.date || t.window || '');
    const m = ds.length >= 7 ? Number(ds.slice(5,7)) : NaN; // YYYY-MM
    if (m >= 1 && m <= 12) months[m-1] += 1;
  }
  return {
    labels: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
    datasets: [{ label: 'Shows by Month', data: months }]
  };
},
recentBadges() {
    return [...(this.gamify.badges || [])].sort((a,b) => b.ts - a.ts).slice(0,5);
  }

},


  mounted() {
   this.$nextTick(async () => {
    this.loadSavedForecasts();
    this.suggestSpotlightArtist();
    // If you already have points on page load, draw immediately
    if (this.hasMapPoints) {
      await this.ensureGoogleMaps();
      this.drawMap();
    }
    
  });
  try {
    this._sfx.cardOpen = new Audio(sfxCardOpenUrl);
    this._sfx.cardOpen.preload = 'auto';
    this._sfx.cardComplete = new Audio(sfxCardCompleteUrl);
    this._sfx.cardComplete.preload = 'auto';
    this._sfx.xpTick = new Audio(sfxXpTickUrl);
    this._sfx.xpTick.preload = 'auto';
  } catch (e) { /* non-fatal */ }
  this.initSfx();
  },

watch: {
 reportObj: {
    deep: true,
    handler() {
      // Re-run KPIs if pastTours changed; then redraw map
      if (Array.isArray(this.reportObj?.pastTours)) {
        this.buildKpis(this.reportObj.pastTours);
      }
      this.queueMapRedraw();
    }
  },
  mapMode() { this.queueMapRedraw(); },
  mapYear() { this.queueMapRedraw(); },
  'reportObj.pastTours': {
    deep: true,
    handler(val) { this.buildKpis(val); this.queueMapRedraw(); }
  },
  'reportObj.futureTours': {
    deep: true,
    handler() { if (this.mapMode === 'predicted') this.queueMapRedraw(); }
  },
  scanProgress(p) {
    if (p >= 100) {
      // small delay to let the 100% animation play
      setTimeout(() => { this.aiLoading = false; }, 900);
    }
  }
},

  methods: {
    sleep(ms){ return new Promise(r=>setTimeout(r,ms)); },

startTickLoop(gapMs = 160, vol = 0.45) {
  this.stopTickLoop();
  this.tickLoop = setInterval(() => {
    this.playSfx('xpTick', { volume: vol, allowOverlap: false });
  }, gapMs);
},
stopTickLoop() {
  if (this.tickLoop) { clearInterval(this.tickLoop); this.tickLoop = null; }
},

  async animateCardFill(key, ms = 900) {
    this.setFilling(key, true);
    this.startTickLoop(150, 0.42);
    await this.sleep(ms);
    this.stopTickLoop();
    this.setFilling(key, false);
  },
debouncedDraw() {
  clearTimeout(this.mapDrawTimer);
  this.mapDrawTimer = setTimeout(() => {
    if (typeof this.ensureGoogleMaps === 'function' && typeof this.drawMap === 'function') {
      Promise.resolve(this.ensureGoogleMaps()).then(() => this.drawMap());
    } else if (typeof this.refreshMap === 'function') {
      this.refreshMap();
    }
  }, 80);
},
debugDump() {
    // Example: inspect a possibly-circular object
    console.log(JSON.stringify({
      forecast: this.forecast,
      freq: this.forecast?.frequencyData
    }, decycle(), 2));
  },
    suggestSpotlightArtist() {
      const arr = this.spotlightArtistsClean;
      this.manualArtist = arr[Math.floor(Math.random() * arr.length)];
    },
    setStepStatus(i, status) {
      this.researchSteps = this.researchSteps.map((s, idx) => (idx === i ? { ...s, status } : s));
    },
    async animateProgress(duration = 2000) {
      const start = Date.now();
      while (Date.now() - start < duration) {
        this.scanProgress = Math.min(100, ((Date.now() - start) / duration) * 100);
        await this.sleep(50);
      }
      this.scanProgress = 100;
    },
    // Google Maps SVG dot marker
getGmDotIcon(color = '#7aa8ff') {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 6,                 // size of the dot
    fillColor: color,
    fillOpacity: 1,
    strokeColor: '#0a2a3a',   // subtle dark edge
    strokeWeight: 1.5
  };
},
_inferPlatform(u = '') {
    try {
      const h = new URL(u).hostname.replace(/^www\./i, '');
      if (h.includes('instagram')) return 'instagram';
      if (h.includes('twitter') || h.includes('x.com')) return 'twitter';
      if (h.includes('tiktok')) return 'tiktok';
      if (h.includes('youtube')) return 'youtube';
      if (h.includes('facebook')) return 'facebook';
      if (h.includes('spotify')) return 'spotify';
      if (h.includes('soundcloud')) return 'soundcloud';
      if (h.includes('bandsintown')) return 'bandsintown';
      if (h.includes('linktr')) return 'linktree';
      return 'website';
    } catch { return 'website'; }
  },

  _prettyPlatform(p = '') {
    const m = {
      twitter: 'Twitter / X',
      x: 'Twitter / X',
      instagram: 'Instagram',
      tiktok: 'TikTok',
      youtube: 'YouTube',
      facebook: 'Facebook',
      spotify: 'Spotify',
      soundcloud: 'SoundCloud',
      bandsintown: 'Bandsintown',
      linktree: 'Linktree',
      website: 'Website'
    };
    return m[(p || '').toLowerCase()] || (p || 'Website');
  },

  _inferHandle(u = '') {
    try {
      const url = new URL(u);
      // handle from first pathname segment
      const seg = url.pathname.split('/').filter(Boolean)[0] || '';
      if (!seg) return '';
      // avoid generic segments like 'channel' on YouTube
      if (/^channel|c|user$/i.test(seg)) return '';
      return seg;
    } catch { return ''; }
  },

  // Tiny SVG badge as data URI (fast, no external assets)
  _socialIconDataUri(p = '') {
    const key = (p || '').toLowerCase();
    const COLORS = {
      instagram: '#E1306C',
      twitter:   '#1DA1F2',
      x:         '#1DA1F2',
      tiktok:    '#000000',
      youtube:   '#FF0000',
      facebook:  '#1877F2',
      spotify:   '#1DB954',
      soundcloud:'#FF7700',
      bandsintown:'#41E0E7',
      linktree:  '#39E09B',
      website:   '#8A8F98'
    };
    const LABELS = {
      instagram: 'IG', twitter: 'X', x: 'X', tiktok: 'TT', youtube: 'YT',
      facebook: 'FB', spotify: 'SP', soundcloud: 'SC', bandsintown: 'BI',
      linktree: 'LT', website: 'WWW'
    };
    const bg = encodeURIComponent(COLORS[key] || COLORS.website);
    const txt = encodeURIComponent(LABELS[key] || 'WWW');

    // Simple rounded badge with text
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <defs><filter id="s" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.35"/>
        </filter></defs>
        <rect x="2" y="2" rx="12" ry="12" width="52" height="52" fill="${bg}" filter="url(#s)"/>
        <text x="50%" y="54%" text-anchor="middle" font-size="22" font-family="Arial,Helvetica,sans-serif"
              fill="#ffffff" font-weight="700">${txt}</text>
      </svg>`;

    return `data:image/svg+xml;utf8,${svg.replace(/\s+/g,' ')}`;
  },
// Leaflet HTML marker (sharp at any zoom)
getLeafletDivIcon(color = '#7aa8ff') {
  const html = `
    <span style="
      display:inline-block;width:12px;height:12px;border-radius:50%;
      background:${color};box-shadow:0 0 0 1.5px #0a2a3a, 0 0 8px rgba(0,0,0,.35);
    "></span>`;
  return window.L.divIcon({
    className: 'tpd-dot',  // no global CSS required
    html,
    iconSize: [12, 12],
    iconAnchor: [6, 6]     // center the dot
  });
},
async initSfx() {
  try {
    // Single audio context
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.sfxCtx = ctx;
    unlockOnFirstGesture(ctx);

    const load = async (url) => {
      const res = await fetch(url);
      const arr = await res.arrayBuffer();
      return await ctx.decodeAudioData(arr);
    };

    const [openBuf, completeBuf, tickBuf] = await Promise.all([
      load(this.sfxCardOpenUrl || sfxCardOpenUrl),
      load(this.sfxCardCompleteUrl || sfxCardCompleteUrl),
      load(this.sfxXpTickUrl || sfxXpTickUrl)
    ]);

    this.sfxBuffers = { cardOpen: openBuf, cardComplete: completeBuf, xpTick: tickBuf };
  } catch (e) {
    // Fallback: keep your existing HTMLAudio refs in this.sfx
    if (typeof console !== 'undefined') console.debug('[SFX] WebAudio unavailable, falling back to <audio>', e);
  }
},

async playSfx(name, { volume = 0.85, allowOverlap = false } = {}) {
  if (!this.sfxEnabled) return;

  const now = performance.now();
  const minGap = SFX_MIN_GAP_MS[name] ?? 120;
  const last = this.sfxLastAt[name] || 0;
  if (!allowOverlap && (now - last) < minGap) return;  // gate

  // WebAudio path
  if (this.sfxCtx && this.sfxBuffers[name]) {
    this.sfxLastAt[name] = now;
    const ctx = this.sfxCtx;
    const src = ctx.createBufferSource();
    const gain = ctx.createGain();
    src.buffer = this.sfxBuffers[name];
    // gentle envelope to avoid clicks
    const t = ctx.currentTime;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(volume, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + src.buffer.duration - 0.02);

    src.connect(gain).connect(ctx.destination);
    return new Promise((resolve) => {
      src.onended = resolve;
      try { src.start(0); } catch { resolve(); }
    });
  }

  // Fallback: HTMLAudio element (your existing this.sfx[name])
  const el = this.sfx?.[name];
  if (!el) return;
  this.sfxLastAt[name] = now;
  try {
    el.currentTime = 0;
    el.volume = volume;
    await el.play();
  } catch { /* no-op */ }
},


play(name) {
  if (!this.sfxEnabled) return;
  const ref = {
    ping:  this.$refs.sfxPing,
    badge: this.$refs.sfxBadge,
    point: this.$refs.sfxPoint
  }[name];
try {
  if (ref && ref.currentTime != null) ref.currentTime = 0;
  if (ref && typeof ref.play === 'function') ref.play();
} catch (e) {
  // Swallow non-fatal audio errors; keep block non-empty for eslint
  void e;
}
},

logActivity(text, icon = '📝') {
  this.activityFeed.unshift({ ts: Date.now(), icon, text });
  // keep it lean
  if (this.activityFeed.length > 50) this.activityFeed.length = 50;
},

award(points = 5, combo = 1) {
  const gain = Math.max(1, Math.round(points * Math.max(1, combo)));
  this.gamify.score += gain;

  // micro xptick burst on notable gains
  if (gain >= 10) this.xpBurstTick(4, 110);

  const nextLevel = Math.floor(this.gamify.score / 250) + 1;
  if (nextLevel > this.gamify.level) {
    this.gamify.level = nextLevel;
    this.giveBadge(`level-${nextLevel}`, `Level ${nextLevel} Reached`, `Lv ${nextLevel}`, '🔺');
    this.play('ping');
    this.logActivity(`Reached Level ${nextLevel}`, '🔺');
  } else {
    this.play('point');
  }
  this.pulseHud();
},


giveBadge(id, label, short, icon = '🏅') {
  if (this.gamify.badges.find(b => b.id === id)) return;
  const badge = { id, label, short, icon, ts: Date.now() };
  this.gamify.badges.push(badge);
  this.play('badge');
  this.logActivity(`Badge earned: ${label}`, icon);
},

checkMilestones() {
  // — Knowledge thresholds
  const k = this.gamify.knowledgeReceived;
  if (k >= 100) this.giveBadge('k-100', 'First 100 Knowledge Items', '100', '🏅');
  if (k >= 500) this.giveBadge('k-500', '500 Knowledge Milestone', '500', '🏆');
  if (k >= 1000) this.giveBadge('k-1k', '1K Knowledge Legend', '1K', '👑');

  // — Searches
  const s = this.gamify.searchesSubmitted;
  if (s >= 1)  this.giveBadge('s-1', 'First Research', '1st', '🎯');
  if (s >= 10) this.giveBadge('s-10', '10 Research Sessions', '10', '🔥');
  if (s >= 25) this.giveBadge('s-25', '25 Research Sessions', '25', '🚀');

  // — Archive / Neural
  const a = this.gamify.archivedCount;
  const n = this.gamify.neuralCount;
  if (a >= 1)  this.giveBadge('a-1', 'First Archive Save', 'Archive', '🗄️');
  if (n >= 1)  this.giveBadge('n-1', 'First Neural Save', 'Neural', '🧠');
},

timeago(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
},


playLocal(name) {
  const el = this.sfx?.[name];
  if (!el) return;
  try {
    el.currentTime = 0;
    el.play?.();
  } catch { /* ignore */ }
},



// replace your setters with this:
setRevealFlag(key, v) {
  if (!hasOwn(this.reveal, key)) return;
  this.$set(this.reveal, key, v);
},
setFilling(key, v) {
  if (!hasOwn(this.filling, key)) return;
  this.$set(this.filling, key, v);
},
setCompleted(key, v) {
  if (!Object.prototype.hasOwnProperty.call(this.completed, key)) return;
  this.$set(this.completed, key, v);
  if (v) this.playSfx('cardComplete');
},


pulseHud(ms=650){
  this.hudPulse = true;
  setTimeout(()=> this.hudPulse = false, ms);
},
xpBurstTick(times = 3, gapMs = 140) {
  let i = 0;
  const step = () => {
    this.xpBurst = true;
    this.playSfx('xpTick', { volume: 0.55, allowOverlap: false });
    setTimeout(() => { this.xpBurst = false; }, 170);
    if (++i < times) setTimeout(step, gapMs);
  };
  step();
},

bumpSearchStreak() {
  const today = new Date(); today.setHours(0,0,0,0);
  const last = this.gamify.lastSearchAt || 0;
  const lastDate = new Date(last); lastDate.setHours(0,0,0,0);

  if (!last || (today - lastDate) > 24*3600*1000) {
    // new day
    this.gamify.streak = (today - lastDate <= 48*3600*1000) ? (this.gamify.streak + 1) : 1;
  }
  this.gamify.lastSearchAt = Date.now();
},

// REPLACE the old stageRevealSequence()
async stageRevealSequence() {
  const stages = ['profile','contacts','charts','analysis','predictions'];

  for (const key of stages) {
    // guards
    if (key === 'contacts'    && !(this.reportObj?.contacts?.length)) continue;
    if (key === 'charts'      && !((this.reportObj?.pastTours||[]).length)) continue;
    if (key === 'analysis'    && !(this.analysisSummary || this.analysisDetails || this.reportObj?.analysis)) continue;
    if (key === 'predictions' && !((this.reportObj?.futureTours||[]).length)) continue;

    // 1) Reveal + open chime (await so it doesn't collide with ticks)
    this.setRevealFlag(key, true);
    // await this.playSfx('cardOpen', { volume: 0.9 });

    // 2) Fill animation with soft gated ticks
    await this.$nextTick();
    await this.animateCardFill(key, 900);

    // 3) Complete + complete chime (await so it doesn't collide with previous)
    this.setCompleted(key, true);
    await this.playSfx('cardComplete', { volume: 0.35 });
    this.pulseHud(600);
    this.xpBurstTick(3, 160);

    // 4) Cooldown before next card (timer bar if you added it)
    await this.sleep(2000);
  }
},



// --- Sound (WebAudio + fallback) ---
playCue(kind = 'click') {
  try {
    if (!this._ctx) this._ctx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = this._ctx;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    const now = ctx.currentTime;

    // simple tones
    if (kind === 'success') { o.frequency.value = 740; }
    else if (kind === 'photo') { o.frequency.value = 523; }
    else { o.frequency.value = 420; }

    g.gain.setValueAtTime(0.001, now);
    g.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    o.start(now);
    o.stop(now + 0.2);
  } catch {
    const el = (kind === 'success') ? this.$refs.sfxSuccess
            : (kind === 'photo')   ? this.$refs.sfxPhoto
            : this.$refs.sfxClick;
    el && el.play && el.play().catch(()=>{});
  }
},

// --- Reveal flow ---
resetReveal() {
  this.reveal = { profile:false, contacts:false, charts:false, analysis:false, predictions:false };
},


// --- Archive / Neural actions ---
sendToArchive() {
  // your real save can go here
  this.gamify.archiveCount += 1;
  this.award(10, 8);
  this.playCue('success');
  this.notify('Saved to Archive', 'info');
},
sendToNeural() {
  // your real ingest can go here
  this.gamify.neuralSaved += 1;
  this.award(12, 10);
  this.playCue('success');
  this.notify('Sent to Neural Brain', 'info');
},

async startResearch() {
  if (this.aiLoading) return;
  const name = this.manualArtist || this.selectedArtist;
  if (!name) { this.notify('Please enter an artist first.', 'error'); return; }

  this.errorMessage = '';
  this.forecast = null;
  this.aiLog = { steps: [] };
  this.artistImg = '';
  this.playCue('photo');
  this.aiLoading = true;                 // single source of truth for overlay
  this.gamify.searchesSubmitted += 1;
this.logActivity(`Started research for “${name}”`, '🔎');
  this.researchSteps.forEach(s => (s.status = 'pending'));
  this.scanProgress = 0;
  this.scanStatusText = 'Initializing…';
this.gamify.searches += 1;
this.bumpSearchStreak();
this.award(5, 5);
this.pulseHud();
this.resetReveal();       // reset staged sections
this.playCue('click');

  if (this.researchWatchdog) {
    clearTimeout(this.researchWatchdog);
    this.researchWatchdog = null;
  }
  this.researchWatchdog = setTimeout(() => {
    if (this.aiLoading) {
      this.notify('Timed out while researching. Closing overlay for responsiveness.', 'error');
      this.aiLoading = false;
    }
  }, 35000);

  const t0 = performance.now();
  const stamp = (label) => this.aiLog.steps.push({ label, t: Math.round(performance.now() - t0) });

  try {
    this.setStepStatus(0, 'in progress');
    this.scanStatusText = '🔎 Finding artist…';
    this.scanProgress = 10; stamp('init');

    let artist = null;
    try {
      const token = await this.getSpotifyToken();
      const res = await axios.get(
        `https://api.spotify.com/v1/search?q=${encodeURIComponent(name)}&type=artist&limit=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      artist = res.data?.artists?.items?.[0] || null;
      if (!artist) throw new Error('Artist not found on Spotify.');
      this.artistData = artist;
      this.setStepStatus(0, 'done');
      this.scanProgress = 25; stamp('spotify:artist');
    } catch (e) {
      this.setStepStatus(0, 'done');
      throw e; // fail early if we truly can’t identify the artist
    }

    this.setStepStatus(1, 'in progress');
    this.scanStatusText = '📷 Fetching artist image…';
    try {
      this.artistImg = artist.images?.[0]?.url || this.placeholderImg;
    } catch (e) {
      this.artistImg = this.placeholderImg;
    }
    this.setStepStatus(1, 'done');
    this.scanProgress = 35; stamp('spotify:image');

    // STEP 3 — OpenAI research (parallel)
    this.setStepStatus(2, 'in progress');
    this.scanStatusText = '🤖 Running AI research…';
    this.scanProgress = 45;

    let aiPast = [];
    let aiContacts = [];
    let aiProfile = {};
    try {
      const p1 = this.aiPastTours(artist.name).catch(() => []);
      const p2 = this.aiContacts(artist.name).catch(() => []);
      const p3 = this.aiArtistProfile(artist.name).catch(() => ({}));
      [aiPast, aiContacts, aiProfile] = await Promise.all([p1, p2, p3]);

      // Normalize/de-dupe AI past tours
      aiPast = this.dedupeTours((aiPast || []).map(t => ({ ...t, source: t.source || 'OpenAI' })));
      this.scanProgress = 60; stamp('openai:research');
    } catch (e) {
      console.warn('[AI research] partial failure:', e);
    } finally {
      this.setStepStatus(2, 'done');
      this.scanStatusText = '📡 Gathering live events…';
    }

    // STEP 4 — Live events (Ticketmaster + Setlist.fm)
    this.scanStatusText = '📡 Gathering events (Ticketmaster + Setlist.fm)…';
    let tmEvents = [];
    let slEvents = [];
    try {
      [tmEvents, slEvents] = await Promise.all([
        this.getTicketmasterEvents(artist.name).catch(() => []),
        this.getSetlistFmData(artist.name).catch(() => [])
      ]);
      this.scanProgress = 75; stamp('live:events');
    } catch (e) {
      // soft-fail
    }

    // —— Merge (declare then assign to avoid TDZ/shadowing) ——
    let mergedPast = this.dedupeTours([
      ...(aiPast || []),
      ...(tmEvents || []),
      ...(slEvents || [])
    ]);

    // —— Geocode missing coords BEFORE using them for stats/map/predictions ——
    mergedPast = await this.geocodeToursIfNeeded(mergedPast);

    // Stats
    const yearCounts = {};
    for (const e of mergedPast) {
      const y = String(e.date || e.window || '').slice(0, 4);
      if (y) yearCounts[y] = (yearCounts[y] || 0) + 1;
    }
    const tourStats = { byYear: yearCounts, totalPast: mergedPast.length };
    this.tourStats = tourStats;

    // STEP 5 — OpenAI predictions (then geocode)
    this.scanStatusText = '🔮 Predicting likely tour windows…';
    this.scanProgress = 82;
    let aiPred = [];
    try {
      aiPred = await this.aiPredictTours(artist.name, mergedPast, tourStats);
      aiPred = this.dedupeTours(aiPred || []);
      aiPred = await this.geocodeToursIfNeeded(aiPred); // so the Predicted map toggle works
      stamp('openai:predict');
    } catch (e) {
      console.warn('[AI predictions] failed:', e);
      aiPred = [];
    }

    // Bind to component state for other features
    this.pastConcerts = mergedPast;
    this.futureTours  = aiPred;

    // STEP 6 — Compose report & enrich with Spotify
    this.reportObj = this.reportObj || { primaryName: artist.name };
    this.reportObj.pastTours   = mergedPast; // use geocoded arrays
    this.reportObj.futureTours = aiPred;
    this.buildKpis(this.reportObj.pastTours);
    // Bind to component state for other features
this.reportObj = this.reportObj || { primaryName: artist.name };
this.reportObj.pastTours   = mergedPast; // use geocoded arrays
this.reportObj.futureTours = aiPred;
this.buildKpis(this.reportObj.pastTours);
this.reportObj.contacts    = aiContacts || [];
this.reportObj.profile     = Object.assign({}, aiProfile || {}, this.reportObj.profile || {});

// 👉 Add the gamify “knowledge received” + staged reveal here:
// already in your file:
const newKnowledge =
  (Array.isArray(mergedPast) ? mergedPast.length : 0) +
  (Array.isArray(aiPred) ? aiPred.length : 0) +
  (Array.isArray(aiContacts) ? aiContacts.length : 0) +
  Object.keys(aiProfile || {}).length;

this.gamify.knowledgeReceived += newKnowledge;
this.award(Math.min(30, Math.round(newKnowledge / 5)), 15);
this.stageRevealSequence();   // animate in main cards

// ✨ NEW:
this.checkMilestones();
this.logActivity(`+${newKnowledge} knowledge items collected`, '📚');


// AI long-form analysis (non-fatal)
try {
  const deep = await this.aiDeepAnalysis(artist.name, {
    pastTours: mergedPast,
    futureTours: aiPred,
    stats: tourStats,
    spotify: this.artistData
  });
  if (deep) {
    this.reportObj.analysis = deep;
  }
} catch (e) {
  if (typeof console !== 'undefined') console.debug('deep analysis skipped', e);
}

try {
  const deep = await this.aiDeepAnalysis(artist.name, {
    pastTours: mergedPast,
    futureTours: aiPred,
    stats: tourStats,
    spotify: this.artistData
  });
  if (deep) {
    this.reportObj.analysis = deep;
  }
} catch (e) {
  if (typeof console !== 'undefined') console.debug('deep analysis skipped', e);
}

    // Spotify enrichment (followers/popularity/social link)
    try {
      if (this.artistData) {
        this.reportObj.audience = this.reportObj.audience || {};
        this.reportObj.audience.platforms = Object.assign({}, this.reportObj.audience.platforms, {
          spotifyFollowers: this.artistData.followers?.total ?? null,
          spotifyPopularity: this.artistData.popularity ?? null
        });
        const spUrl = this.artistData.external_urls?.spotify || null;
        if (spUrl) {
          this.reportObj.socials = Array.isArray(this.reportObj.socials) ? this.reportObj.socials : [];
          if (!this.reportObj.socials.find(s => s === spUrl || s?.url === spUrl)) {
            this.reportObj.socials.unshift(spUrl);
          }
        }
      }
    } catch (e) {
      if (typeof console !== 'undefined') console.debug('spotify enrichment skipped', e);
    }

    // STEP 7 — Build forecast visuals; validate fallback
    this.scanStatusText = '🗺️ Building charts & map…';
    this.buildForecastFromEvents(artist.name, mergedPast, aiPred);

    const check = this.validateForecast(this.forecast);
    if (!check.ok) {
      console.warn('[forecast] invalid:', check.reason);
      this.buildForecastFromEvents(artist.name, mergedPast);
    }

    // Readable analysis blurb
    this.analysisSummary = this.forecast?.pattern_summary
      || 'Blended OpenAI + Ticketmaster + Setlist.fm patterns.';
    this.analysisDetails = `
      <ul>
        <li><strong>Total past shows:</strong> ${this.tourStats?.totalPast || 0}</li>
        <li><strong>Peak touring years:</strong> ${
          Object.entries(this.tourStats?.byYear || {})
            .sort((a,b)=>b[1]-a[1]).slice(0,3).map(([y,c])=>`${y} (${c})`).join(', ') || '—'
        }</li>
      </ul>
    `;

    this.scanProgress = 96; stamp('build:visuals');

    // STEP 8 — Persist & map draw
    this.saveForecastLocally();

    // Use the new computed points/toggle (hasMapPoints), not hasHeatmapPoints
    if (this.hasMapPoints) {
      await this.ensureGoogleMaps();
      this.$nextTick(() => this.drawMap());
    }

    this.scanProgress = 100;
    this.setStepStatus(3, 'done');
    this.scanStatusText = '✅ Forecast Complete';
    this.notify(`Research complete for ${artist.name}!`, 'success');
    stamp('done');
   // Auto-save the freshly computed snapshot
this.saveForecastLocally();

  } catch (err) {
    console.error(err);
    this.errorMessage = err?.message || String(err);
    this.notify('Research failed: ' + this.errorMessage, 'error');
  } finally {
    try {
      await this.sleep(300);
    } catch (e) {
      if (typeof console !== 'undefined') console.debug('sleep aborted', e);
    }
    if (this.researchWatchdog) {
      clearTimeout(this.researchWatchdog);
      this.researchWatchdog = null;
    }
    this.aiLoading = false;
  }
},
buildKpis(pastTours = []) {
    this.kpisLoading = true;
    try {
      const arr = Array.isArray(pastTours) ? pastTours : [];
      const totalShows = arr.length;

      const citySet = new Set();
      const countrySet = new Set();
      const yearSet = new Set();
      let capSum = 0; let capCount = 0;

      for (const t of arr) {
        if (t.city) citySet.add(String(t.city).trim().toLowerCase());
        if (t.country) countrySet.add(String(t.country).trim().toLowerCase());

        const raw = String(t.date || t.window || '');
        const y = raw.slice(0, 4);
        if (/^\d{4}$/.test(y)) yearSet.add(y);

        const cap = Number(t.capacity);
        if (Number.isFinite(cap) && cap > 0) { capSum += cap; capCount++; }
      }

      this.kpis = {
        totalShows,
        uniqueCities: citySet.size,
        uniqueCountries: countrySet.size,
        activeYears: yearSet.size,
        avgCapacity: capCount ? (capSum / capCount) : null
      };
    } finally {
      this.kpisLoading = false;
    }
  },
  async refreshMap() {
  // If no points, just clear current overlays and bail
  if (!this.hasMapPoints) {
    this._clearMapArtifactsSafe();
    return;
  }
  await this.ensureGoogleMaps();
  this.drawMap();
},
async aiDeepAnalysis(artistName, { pastTours = [], futureTours = [], stats = {}, spotify = {} } = {}) {
  const user = `
You are a touring intelligence analyst. Create an executive analysis for "${artistName}".
Use the JSON shape below. Be concise, specific, and avoid fabrications—write "unknown" when unsure.

{
  "analysis": {
    "bio": string,
    "touring_patterns": string,
    "touring_team": { "management": string, "label": string, "agent": string, "promoter": string, "notes": string },
    "audience_summary": string,
    "competitive_rank": { "tier": "developing"|"mid"|"upper-mid"|"arena"|"stadium"|"global A-list", "rationale": string },
    "risks": string[],
    "opportunities": string[],
    "summary": string
  }
}

CONTEXT_PAST_TOURS (truncated):
${JSON.stringify(pastTours.slice(-150), null, 2)}

CONTEXT_FUTURE_TOURS (truncated):
${JSON.stringify(futureTours.slice(0, 60), null, 2)}

STATS:
${JSON.stringify(stats || {}, null, 2)}

SPOTIFY:
${JSON.stringify({ followers: spotify?.followers?.total ?? null, popularity: spotify?.popularity ?? null }, null, 2)}
`.trim();

  try {
    let data;
    try {
      data = await callOpenAIChat(buildChatBody({
        model: AI_PRIMARY_MODEL,
        user,
        maxTokens: 6000,
        temperature: 0.2
      }));
    } catch {
      data = await callOpenAIChat(buildChatBody({
        model: AI_FALLBACK_MODEL,
        user,
        maxTokens: 6000,
        temperature: 0.2
      }), { retries: 0 });
    }
    const parsed = extractChatJson(data);
    return parsed?.analysis || null;
  } catch (e) {
    console.warn('[AI deep analysis] failed:', e);
    return null;
  }
},
extractYear(dateStr) {
      if (!dateStr) return null;
      // Accept ISO, "YYYY-MM-DD", or any string with a 4-digit year
      const m = String(dateStr).match(/(\d{4})/);
      if (!m) return null;
      const y = Number(m[1]);
      return y >= 1900 && y <= 2100 ? y : null;
    },
    isFiniteLatLng(lat, lng) {
       const la = Number(lat), ln = Number(lng);
 return Number.isFinite(la) && Number.isFinite(ln) &&
        la <= 90 && la >= -90 && ln <= 180 && ln >= -180;
    },
    
    normalizePoint(t) {
      if (!t || typeof t !== 'object') return null;

      // Flexible lat/lng intake
       let lat = null, lng = null;
 if (t.lat != null && t.lng != null) {
   lat = Number(t.lat);
   lng = Number(t.lng);
 } else if (Array.isArray(t.latlng) && t.latlng.length === 2) {
   lat = Number(t.latlng[0]);
   lng = Number(t.latlng[1]);
 }

      // Build label strings
      const parts = [];
      if (t.venue) parts.push(t.venue);
      if (t.city) parts.push(t.city);
      if (t.region) parts.push(t.region);
      if (t.country) parts.push(t.country);
      const subtitle = parts.filter(Boolean).join(' · ');

      // Prefer a tight title: Venue or City, else 'Show'
      const title = t.venue || t.city || 'Show';

      // Source link (validated)
      const source = t.source && /^https?:/i.test(String(t.source)) ? String(t.source) : null;

      return {
        lat, lng,
        title,
        subtitle,
        date: t.date || null,
        capacity: (t.capacity != null && !isNaN(t.capacity)) ? Number(t.capacity) : null,
        source
      };
    },
    maybeUrl(u) {
      if (!u) return null;
      const s = String(u).trim();
      return /^https?:\/\//i.test(s) ? s : null;
    },

    // --- Map lifecycle ---
    initMap() {
      // If already initialized, bail
      if (this.map) return;

      // Prefer Leaflet if present
      if (window.L && typeof window.L.map === 'function') {
        const L = window.L;
        this.map = L.map(this.$refs.tourMap, { zoomControl: true, scrollWheelZoom: true });
        // Base layer (only add once)
        this.mapLayer = L.tileLayer(
          'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          { attribution: '&copy; OpenStreetMap contributors' }
        ).addTo(this.map);

        // Optional clustering if plugin exists
        if (L.markerClusterGroup) {
          this.mapCluster = L.markerClusterGroup();
          this.map.addLayer(this.mapCluster);
        }

        // Sensible default view
        this.map.setView([20, 0], 2);
        return;
      }

      // Fallback to Google Maps if available
      if (window.google && window.google.maps) {
        const gm = window.google.maps;
        this.map = new gm.Map(this.$refs.tourMap, {
          center: { lat: 20, lng: 0 },
          zoom: 2,
          mapTypeControl: false,
          streetViewControl: false
        });
        return;
      }

      // Last resort: simple message
      this.$refs.tourMap.innerHTML =
        '<div style="padding:12px;" class="dim">Map library not found. Please load Leaflet or Google Maps on this page.</div>';
    },

    clearMarkers() {
      if (!this.map) return;

      // Leaflet
      if (window.L && this.map instanceof window.L.Map) {
        if (this.mapCluster) {
          this.mapCluster.clearLayers();
        }
        this.mapMarkers.forEach(m => {
          // before
try {
  m.remove?.();
} catch (e) {
  if (typeof console !== 'undefined' && console.debug) {
    console.debug('Marker remove failed:', e);
  }
}




        });
        this.mapMarkers = [];
        return;
      }

      // Google Maps
      if (window.google && window.google.maps && this.map instanceof window.google.maps.Map) {
        this.mapMarkers.forEach(m => m.setMap(null));
        this.mapMarkers = [];
      }
    },

addMarkers(points) {
  if (!this.map) return;

  // -------- Leaflet path --------
  if (window.L && this.map instanceof window.L.Map) {
    const L = window.L;
    const bounds = [];

    points.forEach(p => {
      // choose color per point (prefer p.kind; fall back to current mode)
      const color =
        MAP_COLORS[(p && p.kind) || this.mapMode] || MAP_COLORS.past;

      const icon = this.getLeafletDivIcon(color);
      const marker = L.marker([p.lat, p.lng], { icon });

      const cap = (p.capacity != null) ? `Capacity: ${p.capacity.toLocaleString()}` : null;
      const date = p.date ? `Date: ${p.date}` : null;
      const src  = p.source ? `<br/><a href="${p.source}" target="_blank" rel="noopener">Source</a>` : '';
      const html = `
        <div style="min-width:200px">
          <strong>${this.escapeHTML(p.title)}</strong><br/>
          <span class="dim">${this.escapeHTML(p.subtitle)}</span><br/>
          ${[date, cap].filter(Boolean).map(x => this.escapeHTML(x)).join('<br/>')}
          ${src}
        </div>`;

      marker.bindPopup(html);

      if (this.mapCluster) this.mapCluster.addLayer(marker);
      else marker.addTo(this.map);

      this.mapMarkers.push(marker);
      bounds.push([p.lat, p.lng]);
    });

    if (bounds.length) {
      const b = L.latLngBounds(bounds);
      if (!this.lastBoundsFit || !this.map.getBounds().contains(b)) {
        this.map.fitBounds(b.pad(0.15));
        this.lastBoundsFit = true;
      }
    }
    return;
  }

  // -------- Google Maps path --------
  if (window.google && window.google.maps && this.map instanceof window.google.maps.Map) {
    const gm = window.google.maps;
    const bounds = new gm.LatLngBounds();

    points.forEach(p => {
      // choose color per point (prefer p.kind; fall back to current mode)
      const color =
        MAP_COLORS[(p && p.kind) || this.mapMode] || MAP_COLORS.past;

      const marker = new gm.Marker({
        position: { lat: p.lat, lng: p.lng },
        map: this.map,
        title: p.title || p.name || '',
        icon: this.getGmDotIcon(color)
      });

      const cap = (p.capacity != null) ? `Capacity: ${p.capacity.toLocaleString()}` : null;
      const date = p.date ? `Date: ${p.date}` : null;
      const src  = p.source ? `<br/><a href="${p.source}" target="_blank" rel="noopener">Source</a>` : '';
      const info = new gm.InfoWindow({
        content: `
          <div style="min-width:220px">
            <strong>${this.escapeHTML(p.title)}</strong><br/>
            <span class="dim">${this.escapeHTML(p.subtitle)}</span><br/>
            ${[date, cap].filter(Boolean).map(x => this.escapeHTML(x)).join('<br/>')}
            ${src}
          </div>`
      });
      marker.addListener('click', () => info.open(this.map, marker));

      this.mapMarkers.push(marker);
      bounds.extend(marker.getPosition());
    });

    if (this.mapMarkers.length) this.map.fitBounds(bounds, 60);
  }
},





    escapeHTML(s) {
      return String(s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },
onArtistSelected(artist) {
  if (this.aiLoading) return;           // ✅ don’t start a parallel run
  this.manualArtist = artist;
  this.startResearch();
},

handleMapModeChange() {
    // rebuild map after mode switch
    this.$nextTick(() => {
      if (this.hasMapPoints) {
        this.ensureGoogleMaps().then(() => this.drawMap());
      }
    });
  },

    // ---------- Public API helpers (no backend) ----------
async getSpotifyToken() {
  try {
    const response = await axios.post(
      'https://accounts.spotify.com/api/token',
      new URLSearchParams({
        grant_type: 'client_credentials',
        client_id: SPOTIFY_CLIENT_ID,
        client_secret: SPOTIFY_CLIENT_SECRET
      }),
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    );
    return response.data.access_token;
  } catch (error) {
    console.error('Error fetching Spotify access token:', error);
    throw error;
  }
},

async getTicketmasterEvents(artistName) {
  try {
    let page = 0;
    let totalPages = null;
    const all = [];
    do {
      const resp = await axios.get('https://app.ticketmaster.com/discovery/v2/events.json', {
        params: { keyword: artistName, size: 200, page, classificationName: 'music', apikey: TICKETMASTER_API_KEY }
      });
      const items = resp.data._embedded?.events || [];
      all.push(...items);
      totalPages = resp.data.page?.totalPages || 0;
      page++;
    } while (page < totalPages && page < 10);

    return all.map(ev => {
      const v = ev._embedded?.venues?.[0] || {};
      return {
        id: ev.id,
        name: ev.name,
        date: ev.dates?.start?.localDate || null,
        venue: v.name || 'Unknown Venue',
        city: v.city?.name || '',
        country: v.country?.name || '',
        lat: v.location?.latitude ? parseFloat(v.location.latitude) : null,
        lng: v.location?.longitude ? parseFloat(v.location.longitude) : null,
        capacity: v.capacity || null,
        source: 'Ticketmaster'
      };
    });
  } catch (e) {
    console.error('Ticketmaster error:', e);
    return [];
  }
},

async getSetlistFmData(artistName) {
  try {
    const searchRes = await axios.get(
      `https://api.setlist.fm/rest/1.0/search/artists?artistName=${encodeURIComponent(artistName)}&p=1`,
      { headers: { 'x-api-key': SETLISTFM_API_KEY, 'Accept': 'application/json' } }
    );
    if (typeof searchRes.data !== 'object') return [];       // <- guard

    const mbid = searchRes.data.artists?.[0]?.mbid;
    if (!mbid) return [];

    const setlistRes = await axios.get(
      `https://api.setlist.fm/rest/1.0/artist/${mbid}/setlists?p=1`,
      { headers: { 'x-api-key': SETLISTFM_API_KEY, 'Accept': 'application/json' } }
    );
    if (typeof setlistRes.data !== 'object') return [];       // <- guard

    const sets = Array.isArray(setlistRes.data.setlist) ? setlistRes.data.setlist : [];
    return sets.map(s => {
      const iso = s.eventDate && /^\d{2}-\d{2}-\d{4}$/.test(s.eventDate)
        ? s.eventDate.split('-').reverse().join('-') : s.eventDate;
      return {
        id: s.id,
        name: `${s.venue?.name || 'Show'} — ${s.tour?.name || ''}`.trim(),
        date: iso || null,
        venue: s.venue?.name || 'Unknown Venue',
        city: s.venue?.city?.name || '',
        country: s.venue?.city?.country?.name || '',
        lat: Number(s.venue?.city?.coords?.lat) || null,
        lng: Number(s.venue?.city?.coords?.long) || null,
        source: 'Setlist.fm'
      };
    });
  } catch (e) {
    console.error('Setlist.fm error:', e);
    return [];
  }
},
_geoCacheGet(key) {
    try {
      const all = JSON.parse(localStorage.getItem('geoCache') || '{}');
      return all[key] || null;
    } catch { return null; }
  },
  _geoCacheSet(key, value) {
    try {
      const all = JSON.parse(localStorage.getItem('geoCache') || '{}');
      all[key] = value;
      localStorage.setItem('geoCache', JSON.stringify(all));
    } catch (e) {
  // Swallowing non-fatal error intentionally to keep UI responsive.
  // eslint-disable-next-line no-console
  if (typeof console !== 'undefined' && console.debug) {
    console.debug('non-fatal error ignored at startResearch finalize', e);
  }
}

  },

  async geocodeToursIfNeeded(tours = []) {
  const out = [];
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  // Geocode at most N items per run to avoid quota spikes
  const MAX_TO_GEOCODE = 200;
  let geocodedCount = 0;

  for (const t of tours) {
    // Already has coords?
    const hasCoords = Number.isFinite(Number(t.lat)) && Number.isFinite(Number(t.lng));
    if (hasCoords) { out.push(t); continue; }

    // Build a robust search string
    const qParts = [t.venue, t.city, t.region, t.country]
      .filter(Boolean)
      .map(x => String(x).trim());
    if (!qParts.length) { out.push(t); continue; }

    const query = qParts.join(', ');
    const cacheKey = query.toLowerCase();

    const cached = this._geoCacheGet(cacheKey);
    if (cached) {
      out.push({ ...t, lat: cached.lat, lng: cached.lng });
      continue;
    }

    if (geocodedCount >= MAX_TO_GEOCODE) {
      // Push as-is; future runs can fill the rest
      out.push(t);
      continue;
    }

    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}`;
      const resp = await fetch(url);
      const data = await resp.json();
      const loc = data?.results?.[0]?.geometry?.location || null;

      if (loc && Number.isFinite(loc.lat) && Number.isFinite(loc.lng)) {
        this._geoCacheSet(cacheKey, { lat: loc.lat, lng: loc.lng });
        out.push({ ...t, lat: loc.lat, lng: loc.lng });
      } else {
        out.push(t);
      }
    } catch (e) {
      out.push(t);
    }

    geocodedCount += 1;
    await sleep(120); // gentle throttle
  }

  return out;
},
// Build a full, serializable snapshot of the current state
getCurrentSnapshot() {
  const fc = this.forecast ? this.normalizeForecast(this.forecast) : null;
  return {
    // core
    artist: fc?.artist || this.reportObj?.primaryName || this.manualArtist || this.selectedArtist || '',
    forecast: fc,
    reportObj: this.reportObj || {},
    tourStats: this.tourStats || {},
    pastConcerts: Array.isArray(this.pastConcerts) ? this.pastConcerts : [],
    futureTours: Array.isArray(this.futureTours) ? this.futureTours : [],

    // external/meta
    artistData: this.artistData || null,
    aiLog: this.aiLog || {},
    artistImg: this.artistImg || '',
    errorMessage: this.errorMessage || '',

    // ui prefs
    mapMode: this.mapMode || 'past',
    mapYear: this.mapYear || 'all',

    // bookkeeping
    schemaVersion: 2,
    savedAt: Date.now()
  };
},

// Apply a snapshot to the current component state and refresh visuals
applySnapshot(snap) {
  if (!snap || typeof snap !== 'object') return;

  // Core
  this.forecast      = snap.forecast || null;
  this.reportObj     = snap.reportObj || { contacts: [] };
  this.tourStats     = snap.tourStats || {};
  this.pastConcerts  = Array.isArray(snap.pastConcerts) ? snap.pastConcerts : [];
  this.futureTours   = Array.isArray(snap.futureTours) ? snap.futureTours : [];

  // External/meta
  this.artistData    = snap.artistData || null;
  this.aiLog         = snap.aiLog || {};
  this.artistImg     = snap.artistImg || '';
  this.errorMessage  = ''; // don't revive old errors

  // UI prefs
  this.mapMode       = snap.mapMode || 'past';
  this.mapYear       = snap.mapYear || 'all';

  // Rebuild/normalize forecast if needed
  if (this.forecast && !this.validateForecast(this.forecast).ok) {
    // Make a best-effort rebuild from stored events
    const past = this.reportObj?.pastTours || this.pastConcerts || [];
    const fut  = this.reportObj?.futureTours || this.futureTours || [];
    this.buildForecastFromEvents(snap.artist || this.forecast.artist || this.manualArtist || '', past, fut);
  }

  // Redraw charts/map after the DOM updates
  this.$nextTick(() => this.queueMapRedraw());
},

// Past tours (needs more room)
async aiPastTours(artistName) {
  const user = `
Return only JSON with a "pastTours" array (>= 50 shows if possible) for "${artistName}" over ~10 years.
Each item fields: city, venue (nullable), country (nullable), region (nullable), date (YYYY-MM-DD or YYYY-MM), capacity (number|null), lat (number|null), lng (number|null), source (string|null).
No commentary. JSON only.
`.trim();

  try {
    let data;
    try {
      data = await callOpenAIChat(buildChatBody({
        model: AI_PRIMARY_MODEL,
        user,
        maxTokens: 9000,  // generous for 50+ rows
        temperature: 0.2
      }));
    } catch {
      data = await callOpenAIChat(buildChatBody({
        model: AI_FALLBACK_MODEL,
        user,
        maxTokens: 9000,
        temperature: 0.2
      }), { retries: 0 });
    }
    const parsed = extractChatJson(data);
    return Array.isArray(parsed?.pastTours) ? parsed.pastTours : [];
  } catch (e) {
    console.warn('[AI past tours] failed:', e);
    return [];
  }
},

// Predictions
async aiPredictTours(artistName, pastTours = [], stats = {}) {
  const user = `
You are a live entertainment analyst.
Given past tours (JSON) and stats, predict 12–20 likely upcoming markets with realistic date windows for "${artistName}".
Return only JSON with:
{
  "artist": string,
  "assumptions": string[],
  "predictions": [
    { "city": string, "venue": string|null, "country": string|null, "region": string|null,
      "window": string, "confidence": number, "capacity": number|null, "lat": number|null, "lng": number|null }
  ]
}
Rules:
- windows like "Jun–Jul 2026" or "Q1 2026" are fine.
- confidence 0.55–0.95.
- capacity is your best venue-size estimate.
- Use past demand, seasonality, gaps, and geography.
PAST_TOURS:
${JSON.stringify(pastTours.slice(-120), null, 2)}

STATS:
${JSON.stringify(stats || {}, null, 2)}
`.trim();

  try {
    let data;
    try {
      data = await callOpenAIChat(buildChatBody({
        model: AI_PRIMARY_MODEL,
        user,
        maxTokens: 7000,
        temperature: 0.2
      }));
    } catch {
      data = await callOpenAIChat(buildChatBody({
        model: AI_FALLBACK_MODEL,
        user,
        maxTokens: 7000,
        temperature: 0.2
      }), { retries: 0 });
    }

    const parsed = extractChatJson(data);
    return Array.isArray(parsed?.predictions) ? parsed.predictions : [];
  } catch (e) {
    console.warn('[AI predictions] failed:', e);
    return [];
  }
},

// Contacts
async aiContacts(artistName) {
  const user = `
Find realistic current contacts for "${artistName}".
Return ONLY JSON: { "contacts": [ { "role": string, "name": string|null, "company": string|null, "email": string|null, "phone": string|null, "website": string|null, "linkedin": string|null, "socials": string[], "notes": string|null } ] }
Avoid fabrications; if unknown, leave null. JSON only.
`.trim();

  try {
    let data;
    try {
      data = await callOpenAIChat(buildChatBody({
        model: AI_PRIMARY_MODEL,
        user,
        maxTokens: 7000,
        temperature: 0.2
      }));
    } catch {
      data = await callOpenAIChat(buildChatBody({
        model: AI_FALLBACK_MODEL,
        user,
        maxTokens: 7000,
        temperature: 0.2
      }), { retries: 0 });
    }

    const parsed = extractChatJson(data);
    return Array.isArray(parsed?.contacts) ? parsed.contacts : [];
  } catch (e) {
    console.warn('[AI contacts] failed:', e);
    return [];
  }
},

// Profile
async aiArtistProfile(artistName) {
  const user = `
Provide a structured profile for "${artistName}".
Return ONLY JSON: { "profile": { "legalName": string|null, "aliases": string[], "origin": string|null, "label": string|null, "management": string|null, "bio": string|null, "awards": string[], "socials": string[], "officialWebsite": string|null } }
Avoid speculation. JSON only.
`.trim();

  try {
    let data;
    try {
      data = await callOpenAIChat(buildChatBody({
        model: AI_PRIMARY_MODEL,
        user,
        maxTokens: 6000,
        temperature: 0.2
      }));
    } catch {
      data = await callOpenAIChat(buildChatBody({
        model: AI_FALLBACK_MODEL,
        user,
        maxTokens: 6000,
        temperature: 0.2
      }), { retries: 0 });
    }

    const parsed = extractChatJson(data);
    return parsed?.profile || {};
  } catch (e) {
    console.warn('[AI profile] failed:', e);
    return {};
  }
},


dedupeTours(list = []) {
  const keyOf = (t) => [t.city||'', t.venue||'', t.date||'', t.window||'']
    .map(s => String(s || '').toLowerCase().trim()).join('|');
  const seen = new Set();
  const out = [];
  for (const t of list) {
    const k = keyOf(t);
    if (!seen.has(k)) { seen.add(k); out.push(t); }
  }
  return out;
},

// Build forecast structures your UI/chart/map already use
buildForecastFromEvents(artistName, ...eventSources) {
  const events = eventSources.flat().filter(e => e && (e.date || e.window));
  // Frequency by year
  const byYear = new Map();
  events.forEach(e => {
    const y = String((e.date || e.window || '')).slice(0,4);
    if (!y) return;
    if (!byYear.has(y)) byYear.set(y, []);
    byYear.get(y).push(e);
  });
  const labels = Array.from(byYear.keys()).sort();
  const data = labels.map(y => byYear.get(y).length);
  const evLists = labels.map(y => byYear.get(y).map(e => ({
    city: e.city || e.location || '', venue: e.venue || null, date: e.date || e.window || null
  })));

  // Heat points (only if we have coords)
  const heat = events
    .filter(e => typeof e.lat === 'number' && typeof e.lng === 'number')
    .map(e => ({
      name: e.venue || `${e.city || ''}${e.country ? ', ' + e.country : ''}`,
      latlng: [e.lat, e.lng],
      count: 1,
      date: e.date || e.window || ''
    }))
    .slice(0, 900);

  this.forecast = {
    artist: artistName,
    pattern_summary: 'Blended from OpenAI research + Ticketmaster + setlist.fm.',
    frequencyData: { labels, datasets: [{ label: 'Tour Shows per Year', data, events: evLists }] },
    heatmapData: heat,
    businessAcquisitionReport: { prg_opportunity_score: 0.65 }
  };
},

queueMapRedraw() {
    clearTimeout(this.mapRedrawTimer);
    this.mapRedrawTimer = setTimeout(() => {
      if (this.hasMapPoints) {
        this.ensureGoogleMaps().then(() => this.drawMap());
      } else if (this.gmap) {
        // clear map if no points
        this._clearMapArtifactsSafe();
      }
    }, 120);
  },

  // (replace) drawMap uses currentMapPoints/mapPoints and robust cleanup
  drawMap() {
    if (!this.$refs.tourMap) return;
    if (!(window.google && window.google.maps)) {
      this.notify('Map not available right now. Try again later.', 'error');
      return;
    }

    if (!this.gmap) {
      this.gmap = new google.maps.Map(this.$refs.tourMap, {
        center: { lat: 20, lng: 0 },
        zoom: 2,
        gestureHandling: 'greedy',
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        styles: [
          { elementType: 'geometry', stylers: [{ color: '#0b1220' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#cfe6ff' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#0b1220' }] },
          { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0a2033' }] },
          { featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'road', stylers: [{ visibility: 'off' }] }
        ]
      });
    }

    this.renderMarkersAndRoute();
  },

  // (replace) don’t use undefined this.mapPoints; use computed and safe cleanups
  renderMarkersAndRoute() {
  if (!this.gmap) return;

  const points = this.currentMapPoints;
  this._clearMapArtifactsSafe();
  if (!Array.isArray(points) || !points.length) return;

  this.gmMarkers = [];
  const bounds = new google.maps.LatLngBounds();

  for (const p of points) {
    const pos = { lat: p.lat, lng: p.lng };
    const marker = new google.maps.Marker({
      position: pos,
      map: this.gmap,
      title: p.title || ''
    });

    // Build rich popup content from Past Tours
    const cap  = (p.capacity != null) ? `Capacity: ${p.capacity.toLocaleString()}` : null;
    const date = p.date ? `Date: ${p.date}` : null;
    const src  = p.source ? `<br/><a href="${p.source}" target="_blank" rel="noopener">Source</a>` : '';

    const info = new google.maps.InfoWindow({
      content: `
        <div style="min-width:220px">
          <strong>${this.escapeHTML(p.title || '')}</strong><br/>
          <span class="dim">${this.escapeHTML(p.subtitle || '')}</span><br/>
          ${[date, cap].filter(Boolean).map(x => this.escapeHTML(x)).join('<br/>')}
          ${src}
        </div>`
    });
    marker.addListener('click', () => info.open(this.gmap, marker));

    this.gmMarkers.push(marker);
    bounds.extend(marker.getPosition());
  }

  if (!bounds.isEmpty()) {
    this.gmap.fitBounds(bounds);
  }

  // Optional route line with animation
  if (points.length >= 2) {
    if (this.gmapPolyline) {
      try {
  this.gmapPolyline.setMap(null);
} catch (e) {
  if (typeof console !== 'undefined' && console.debug) {
    console.debug('polyline cleanup skipped:', e);
  }
}
    }
    this.gmapPolyline = new google.maps.Polyline({
      path: points.map(p => ({ lat: p.lat, lng: p.lng })),
      geodesic: true,
      strokeOpacity: 1,
      strokeWeight: 4
    });
    this.gmapPolyline.setMap(this.gmap);
    this.animatePolyline();
  }
},

  // (replace) safe cleanup without empty catches, and no underscores
  _clearMapArtifactsSafe() {
    if (Array.isArray(this.gmMarkers)) {
      for (const m of this.gmMarkers) {
        if (m && typeof m.setMap === 'function') {
          try { m.setMap(null); } catch (e) { if (console?.debug) console.debug('marker cleanup', e); }
        }
      }
    }
    this.gmMarkers = [];

    if (this.gmAnimRAF) {
      cancelAnimationFrame(this.gmAnimRAF);
      this.gmAnimRAF = null;
    }
    if (this.gmapPolyline && typeof this.gmapPolyline.setMap === 'function') {
      try { this.gmapPolyline.setMap(null); } catch (e) { if (console?.debug) console.debug('polyline cleanup', e); }
      this.gmapPolyline = null;
    }
  },

async ensureGoogleMaps({ timeoutMs = 28000, retries = 1 } = {}) {
    // already ready?
    if (window.google && window.google.maps) {
      this.gmapLoaded = true;
      return;
    }

    // single-flight
    if (this.gmapLoaderPromise) {
      await this.gmapLoaderPromise;
      return;
    }

    // if a script exists but API not ready yet, just wait on it
    const existing = document.getElementById('gmap-sdk');
    if (existing && !(window.google && window.google.maps)) {
      this.gmapLoaderPromise = this.waitForGoogleSdkReady(timeoutMs);
      try {
        await this.gmapLoaderPromise;
        this.gmapLoaded = true;
        return;
      } catch (e) {
        // fall through to full reload below
      } finally {
        this.gmapLoaderPromise = null;
      }
    }
    if (existing && window.google && window.google.maps) {
      this.gmapLoaded = true;
      return;
    }

    // fresh load with retry
    let attempt = 0;
    const loadOnce = () => new Promise((resolve, reject) => {
      const cbName = `__onGoogleMapsReady_${Date.now()}`;
      const cleanup = () => {
        try { delete window[cbName]; } catch { window[cbName] = undefined; }
        clearTimeout(timer);
      };

      window[cbName] = () => {
        cleanup();
        // extra defensive poll after callback fires
        this.waitForGoogleSdkReady(6000).then(resolve).catch(reject);
      };

      const script = document.createElement('script');
      script.id = 'gmap-sdk';
      script.async = true;
      script.defer = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&callback=${cbName}`;
      script.onerror = () => { cleanup(); reject(new Error('Google Maps network error')); };

      // remove any stale tag first (if browser cached a broken one)
      const stale = document.getElementById('gmap-sdk');
if (stale && stale.parentNode) {
  stale.parentNode.removeChild(stale);
}


      document.head.appendChild(script);

      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Google Maps load timed out'));
      }, timeoutMs);
    });

    while (attempt <= retries) {
      try {
        this.gmapLoaderPromise = loadOnce();
        await this.gmapLoaderPromise;
        this.gmapLoaderPromise = null;
        this.gmapLoaded = true;
        return;
      } catch (e) {
        this.gmapLoaderPromise = null;
        attempt += 1;

        // hard reset between attempts
        if (Object.prototype.hasOwnProperty.call(window, 'google')) {
  try {
    delete window.google;
  } catch (e) {
    // Fallback for environments where delete may fail
    window.google = undefined;
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('window.google delete fallback applied:', e);
    }
  }
}

        const tag = document.getElementById('gmap-sdk');
if (tag && tag.parentNode) {
  tag.parentNode.removeChild(tag);
}


        if (attempt > retries) throw e;
        // small backoff
        await new Promise(r => setTimeout(r, 800 + attempt * 400));
      }
    }
  },

  waitForGoogleSdkReady(waitMs = 12000) {
    return new Promise((resolve, reject) => {
      const t0 = Date.now();
      const tick = () => {
        if (window.google && window.google.maps) return resolve();
        if (Date.now() - t0 > waitMs) return reject(new Error('Google Maps wait timed out'));
        setTimeout(tick, 80);
      };
      tick();
    });
  },

  // (rename) no leading underscore; also ensure RAF handle is renamed
  animatePolyline() {
  if (this.gmAnimRAF) { cancelAnimationFrame(this.gmAnimRAF); this.gmAnimRAF = null; }
  if (!this.gmapPolyline) return;

  let offset = 0;
  const tick = () => {
    if (!this.gmapPolyline) return;
    // ↓↓↓ ~55% slower than before
    offset = (offset + 0.03) % 200;
    this.gmapPolyline.setOptions({
      icons: [{
        icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 1, strokeColor: '#FF0000' },
        offset: offset + '%',
        repeat: '80px'
      }]
    });
    this.gmAnimRAF = requestAnimationFrame(tick);
  };
  this.gmAnimRAF = requestAnimationFrame(tick);
},

// ---------- Forecast validation / normalization ----------
    validateForecast(fc) {
      if (!fc || typeof fc !== 'object') return { ok: false, reason: 'not an object' };
      if (!fc.artist || typeof fc.artist !== 'string') return { ok: false, reason: 'missing artist' };
      if (!fc.frequencyData || !Array.isArray(fc.frequencyData.labels)) return { ok: false, reason: 'missing frequencyData.labels' };
      const ds = fc.frequencyData.datasets;
      if (!Array.isArray(ds) || !ds.length) return { ok: false, reason: 'missing datasets' };
      if (!Array.isArray(ds[0].data)) return { ok: false, reason: 'dataset[0].data missing' };
      if (!Array.isArray(ds[0].events)) return { ok: false, reason: 'dataset[0].events missing' };
      // heatmap is optional but recommended
      return { ok: true };
    },
    normalizeForecast(fc) {
      const out = { ...fc };
      // sanitize heatmap
      if (Array.isArray(out.heatmapData)) {
        out.heatmapData = out.heatmapData
          .filter(p => p && p.name && Array.isArray(p.latlng) && p.latlng.length === 2)
          .map(p => ({
            ...p,
            date: p.date || new Date().toISOString().slice(0, 10)
          }))
          .sort((a, b) => String(a.date).localeCompare(String(b.date)));
      }
      // ensure frequency events alignment length == labels length
      const labels = out.frequencyData?.labels || [];
      const ds0 = out.frequencyData?.datasets?.[0];
      if (ds0 && Array.isArray(ds0.events)) {
        if (ds0.events.length !== labels.length) {
          // pad or trim
          const copy = ds0.events.slice(0, labels.length);
          while (copy.length < labels.length) copy.push([]);
          ds0.events = copy;
        }
      }
      return out;
    },

    // ---------- Charts interaction ----------
    handleHoverYear(index) {
      const labels = this.forecast?.frequencyData?.labels || [];
      const ds0 = this.forecast?.frequencyData?.datasets?.[0];
      if (index != null && labels[index] && ds0) {
        const year = labels[index];
        this.selectedYear = year;
        this.selectedEvents = Array.isArray(ds0.events?.[index]) ? ds0.events[index] : [];
        this.popupActive = true;
      } else {
        this.popupActive = false;
        this.selectedEvents = [];
      }
    },

_waitForGoogle() {
  return new Promise((resolve, reject) => {
    const t0 = Date.now();
    const check = () => {
      if (window.google && window.google.maps) return resolve();
      if (Date.now() - t0 > 12000) return reject(new Error('Google Maps wait timed out'));
      setTimeout(check, 60);
    };
    check();
  });
},


_clearMapArtifacts() {
  // Clear markers
  if (Array.isArray(this._gmMarkers)) {
    for (const m of this._gmMarkers) {
  try {
    m.setMap(null);
  } catch (e) {
    if (typeof console !== 'undefined') console.debug('marker cleanup failed', e);
  }
}

  }
  this._gmMarkers = [];

  // Clear polyline + stop animation
  if (this._gmAnimRAF) {
    cancelAnimationFrame(this._gmAnimRAF);
    this._gmAnimRAF = null;
  }
  if (this.gmapPolyline) {
    try {
  this.gmapPolyline.setMap(null);
} catch (e) {
  if (typeof console !== 'undefined') console.debug('polyline cleanup failed', e);
}

    this.gmapPolyline = null;
  }
},


// Save button handler (kept for template compatibility)
saveForecast() {
  this.saveForecastLocally();
},

saveForecastLocally() {
  if (!this.forecast?.artist) return;

  // Make a chart-data–safe clone of forecast
  const safeForecast = deepClone({
    ...this.forecast,
    // ensure datasets don’t carry Chart.js internals
    frequencyData: this.forecast?.frequencyData
      ? deepClone(this.forecast.frequencyData)
      : null
  });

  const all = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
  all[this.forecast.artist] = {
    forecast: safeForecast,
    log: deepClone(this.aiLog),
    artistImg: this.artistImg || '',
    reportObj: deepClone(this.reportObj || {}), // report can be reactive; clone it
    kpis: deepClone(this.kpis || null),
    savedAt: Date.now()
  };
  localStorage.setItem(this.storageKey, JSON.stringify(all));
  // inside saveForecastLocally(), after localStorage set + this.loadSavedForecasts();
this.gamify.archivedCount += 1;
this.award(20, 2);
this.giveBadge('a-first', 'Archive Initiated', 'Archive', '🗄️');
this.checkMilestones();
this.logActivity(`Saved snapshot of “${this.forecast.artist}” to Archive`, '🗄️');
this.gamify.neuralCount += 1;
this.award(25, 2);
this.giveBadge('n-first', 'Neural Brain Linked', 'Neural', '🧠');
this.checkMilestones();
this.logActivity('Pushed current intel to Neural Brain', '🧠');

  this.loadSavedForecasts();
},


// Show a minimal list in the "Saved Forecasts" section
loadSavedForecasts() {
  const all = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
  const items = Object.entries(all)
    .filter(([k]) => k !== '__lastSaved')
    .map(([artist, snap]) => ({
      artist,
      // small card data
      artistImg: snap?.artistImg || '',
      savedAt: snap?.savedAt || 0,
      // nice to show: totals for quick glance
      totalShows: (snap?.reportObj?.pastTours || snap?.pastConcerts || []).length,
      futureCount: (snap?.reportObj?.futureTours || snap?.futureTours || []).length
    }))
    // newest first
    .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));

  this.savedForecasts = items;
},

loadForecast(artist) {
  const all = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
  const rec = all[artist];
  if (!rec) return;

  this.forecast  = rec.forecast;
  this.aiLog     = rec.log || {};
  this.artistImg = rec.artistImg || '';
  this.reportObj = rec.reportObj || this.reportObj || { contacts: [] };
  this.kpis      = rec.kpis || null;

  // Ensure coords then rebuild KPIs (in case schema evolved)
  Promise.resolve()
    .then(() => this.geocodeToursIfNeeded(this.reportObj.pastTours || []))
    .then(geo => { this.reportObj.pastTours = geo; })
    .then(() => this.buildKpis(this.reportObj.pastTours))
    .finally(() => this.queueMapRedraw());
},

// Remove a snapshot
deleteForecast(artist) {
  const all = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
  delete all[artist];
  if (all.__lastSaved === artist) delete all.__lastSaved;
  localStorage.setItem(this.storageKey, JSON.stringify(all));

  // Update list and clear current view if it matched
  this.loadSavedForecasts();
  if ((this.forecast?.artist || this.reportObj?.primaryName) === artist) {
    this.forecast = null;
    this.reportObj = { contacts: [] };
    this.pastConcerts = [];
    this.futureTours  = [];
    this.tourStats    = {};
    this.artistData   = null;
    this.aiLog        = {};
    this.artistImg    = '';
    this.queueMapRedraw();
  }
},
// Upgrade older snapshot shapes to the latest schema
migrateSnapshot(oldSnap = {}, artistKey = '') {
  // baseline
  const migrated = {
    schemaVersion: 2,
    artist: oldSnap?.forecast?.artist || artistKey || '',
    savedAt: oldSnap?.savedAt || Date.now(),
    forecast: oldSnap?.forecast || null,
    reportObj: oldSnap?.reportObj || {},
    tourStats: oldSnap?.tourStats || {},
    pastConcerts: oldSnap?.pastConcerts || [],
    futureTours: oldSnap?.futureTours || [],
    artistData: oldSnap?.artistData || null,
    aiLog: oldSnap?.aiLog || oldSnap?.log || {},
    artistImg: oldSnap?.artistImg || '',
    errorMessage: '',
    mapMode: oldSnap?.mapMode || 'past',
    mapYear: oldSnap?.mapYear || 'all'
  };

  // If old save never had reportObj but had forecast heatmap or events, reconstruct some
  if (!migrated.reportObj || !Object.keys(migrated.reportObj).length) {
    const past = Array.isArray(migrated.pastConcerts) ? migrated.pastConcerts : [];
    const fut  = Array.isArray(migrated.futureTours)  ? migrated.futureTours  : [];
    migrated.reportObj = {
      primaryName: migrated.artist,
      pastTours: past,
      futureTours: fut,
      contacts: [],
      profile: {},
      analysis: null,
      sources: []
    };
  }

  return migrated;
},


    // ---------- Sales copy (server-proxied) ----------
    async generateEmailPitch(contact) {
      if (!this.forecast || !contact?.email) return;
      const body = {
        kind: 'email',
        artist: this.forecast.artist,
        contact,
        patternSummary: this.forecast.pattern_summary || '',
        likelyRegions: (this.forecast.forecast_windows?.[0]?.likely_regions || []).join(', '),
        opportunityScore: (this.forecast.businessAcquisitionReport?.prg_opportunity_score || 0),
        services: (this.forecast.businessAcquisitionReport?.priorityVenues?.[0]?.recommended_services || [])
      };
      const res = await fetch('/api/sales-copy', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      this.generatedEmail = data?.text || '';
      this.currentContact = contact;
      this.emailDialog = true;
    },
    async generateCallScript(contact) {
      if (!this.forecast || !contact?.phone) return;
      const body = {
        kind: 'call',
        artist: this.forecast.artist,
        contact,
        likelyRegions: (this.forecast.forecast_windows?.[0]?.likely_regions || []).join(', '),
        services: (this.forecast.businessAcquisitionReport?.priorityVenues?.[0]?.recommended_services || [])
      };
      const res = await fetch('/api/sales-copy', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      this.generatedCallScript = data?.text || '';
      this.currentContact = contact;
      this.callDialog = true;
    },

    // ---------- Misc ----------
    notify(msg, type = 'info') {
      // hook for a snackbar/toast system
      console[type === 'error' ? 'error' : 'log'](`[${type}] ${msg}`);
    },
    formatDate(ts) { return new Date(ts).toLocaleString(); }
  }
};
</script>

<style scoped>
/* ===== Futuristic AI Dashboard Theme ===== */
:root { /* works with scoped by scoping to component root */ }

.tpd-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 1920px;
  padding: 60px 60px 60px 60px;
  min-height: 100vh;
  /* Layered animated gradients */
  background:
    radial-gradient(1200px 600px at 0% -10%, rgba(0, 255, 234, 0.12), transparent 60%),
    radial-gradient(900px 500px at 100% 110%, rgba(90, 110, 255, 0.10), transparent 60%),
    linear-gradient(135deg, #0b1220 0%, #0a0f1a 35%, #0b0f18 100%);
  color: #e8f4ff;
}

/* Subtle floating grid overlay */
.tpd-wrapper::before {
  content:"";
  position:absolute; inset:0; pointer-events:none;
  background-image:
    linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 24px 24px, 24px 24px;
  mask-image: radial-gradient(1200px 700px at 50% 50%, black 40%, transparent 100%);
}

/* Utility: glassy-neon card look */
.neo-card {
  background: linear-gradient(180deg, var(--glass-0), var(--glass-1));
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--border);
  border-radius: var(--r-card);
  padding: var(--pad-card);
  margin-block: var(--gap);
  box-shadow: var(--shadow-inset), var(--shadow-card);
  color: #d8ecff;
}

/* Header / Controls */
.tpd-header { display: grid; gap: var(--gap); margin-bottom: 28px; padding-top: 60px; }

.tpd-controls-grid {
  display: grid;
  grid-template-columns: 0.98fr auto;
  gap: var(--gap);
  align-items: center;
  margin-left: 40px;
}
@media (max-width: 760px) {
  .tpd-controls-grid { grid-template-columns: 1fr; }
}

.tpd-search { display: flex; gap: 10px; }

.tpd-input {
  flex: 1;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.15);
  background: rgba(8,14,24,0.8);
  color: #eaf6ff;
  outline: none;
  transition: box-shadow .2s ease, border-color .2s ease;
}
.tpd-input:focus {
  border-color: rgba(110,243,255,0.6);
  box-shadow: 0 0 0 3px rgba(110,243,255,0.15);
}

.tpd-btn {
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(110,243,255,0.6);
  background: linear-gradient(180deg, rgba(20,40,60,0.9), rgba(12,22,36,0.95));
  color: #cfe6ff;
  cursor: pointer;
  transition: transform .08s ease, box-shadow .2s ease;
}
.tpd-btn:hover { box-shadow: 0 0 12px rgba(110,243,255,0.35); }
.tpd-btn:active { transform: translateY(1px) scale(0.99); }

.tpd-btn-ghost {
  background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03));
  color: #bbdefb;
  border: 1px solid rgba(255,255,255,0.16);
}

.tpd-spotlight { display:flex; align-items:center; gap:8px; flex-wrap:wrap; color:#97b3d5; }

.tpd-chip {
  margin: 6px 8px 0 0;
  padding: 8px 12px;
  border-radius: var(--r-chip);
  border: 1px solid var(--chip-br);
  background: var(--chip-bg);
  color: #d8ecff;
  cursor: pointer;
  transition: box-shadow .2s ease, transform .15s ease, border-color .2s ease, color .2s ease;
}
.tpd-chip:hover {
  color: #e5fbff;
  border-color: rgba(110,243,255,0.55);
  box-shadow: 0 0 0 2px rgba(0,255,225,0.35) inset, 0 4px 16px rgba(0,0,0,0.3);
  transform: translateY(-1px);
}

/* Thumb */
.tpd-thumb { width: 90px; height: 90px; border-radius: 50%; overflow: hidden; box-shadow: 0 0 12px rgba(110,243,255,0.25); margin-left: 20px; }
.tpd-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; outline-color: rgba(110,243,255,0.4); }

.tpd-overlay-thumb {
  width: 72px; height: 72px; border-radius: 12px; object-fit: cover; margin: 8px 0 4px; box-shadow: 0 6px 18px rgba(0,0,0,0.25);
}

/* Overlay */
.tpd-overlay { position: fixed; inset: 0; background: rgba(6, 10, 18, 0.65); display: grid; place-items: center; z-index: 999; }
.tpd-overlay-card { width: min(640px, 94vw); border-radius: var(--r-card); }
.tpd-overlay-card { /* merged card style */ background: linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.03)); border:1px solid var(--border); padding: 22px 22px 26px; box-shadow: var(--shadow-inset), var(--shadow-card); }

/* Steps */
.tpd-steps { list-style: none; padding: 0; margin: 12px 0 18px; }
.tpd-step { display:grid; grid-template-columns:28px 1fr; gap:10px; align-items:center; padding:8px 0; }
.tpd-step .tpd-step-index { width:28px; height:28px; display:grid; place-items:center; border-radius:8px; background: rgba(255,255,255,0.08); box-shadow: 0 0 0 1px rgba(255,255,255,0.15) inset; }
/* Works if class is literally "in progress" (two classes) or "in-progress" */
.tpd-step.in.progress .tpd-step-index,
.tpd-step.in-progress .tpd-step-index { background: rgba(253,230,138,0.2); box-shadow: 0 0 0 1px rgba(253,230,138,0.45) inset; }
.tpd-step.done .tpd-step-index { background: rgba(134,239,172,0.2); box-shadow: 0 0 0 1px rgba(134,239,172,0.45) inset; }
.tpd-status { margin-top: 10px; color: #9bc4ff; opacity: 0.8; }

/* Progress (with animated phases) */
.tpd-progress { width: 100%; height: 10px; background: rgba(255,255,255,0.08); border-radius: 999px; overflow: hidden; box-shadow: 0 0 0 1px rgba(255,255,255,0.12) inset; }
.tpd-progress-bar { height: 100%; width: 0%; background: linear-gradient(90deg, var(--accent-2), var(--accent-1)); transition: width 300ms ease; box-shadow: 0 0 10px rgba(57,179,255,0.5); }

/* 60% phase: subtle pulse */
.phase-60 { animation: glow60 1.2s ease-in-out infinite; }
@keyframes glow60 {
  0%, 100% { filter: drop-shadow(0 0 6px rgba(110,243,255,0.55)); }
  50%      { filter: drop-shadow(0 0 14px rgba(110,243,255,0.85)); }
}

/* 80% phase: brighter, faster pulse + shimmer */
.phase-80 {
  animation: glow80 0.9s ease-in-out infinite, shimmer 2.5s linear infinite;
  background: linear-gradient(90deg, #5ff6ff, #8effff, #5ff6ff);
  background-size: 200% 100%;
}
@keyframes glow80 {
  0%, 100% { filter: drop-shadow(0 0 10px rgba(143,255,255,0.65)); }
  50%      { filter: drop-shadow(0 0 22px rgba(143,255,255,0.95)); }
}
@keyframes shimmer {
  0% { background-position: 0% 0; } 100% { background-position: 200% 0; }
}

/* 100% finale: pop + halo sweep */
.phase-100 {
  animation: finalePop 0.65s ease-out forwards, halo 1.2s ease-out forwards;
  background: linear-gradient(90deg, var(--accent-3), #e6ffe6);
}
@keyframes finalePop {
  0% { transform: scaleX(0.98); filter: brightness(1); }
  50% { transform: scaleX(1.02); filter: brightness(1.25); }
  100% { transform: scaleX(1); filter: brightness(1.1); }
}
@keyframes halo {
  0% { box-shadow: 0 0 0 rgba(123,255,178,0.0), 0 0 12px rgba(123,255,178,0.4); }
  100% { box-shadow: 0 0 24px rgba(123,255,178,0.55), 0 0 48px rgba(123,255,178,0.35); }
}

/* Artist summary */
.tpd-artist { display:grid; grid-template-columns: 140px 1fr; gap:18px; align-items:center; margin:22px 0; padding:16px; }
.tpd-artist-img { width:140px; height:140px; object-fit:cover; border-radius:14px; box-shadow: 0 0 0 2px rgba(255,255,255,0.15) inset, 0 12px 28px rgba(0,0,0,0.35); }
.tpd-artist-meta h2, .tpd-artist-meta h4 { margin: 0 0 6px; letter-spacing: 0.3px; }
.tpd-pattern { color: var(--text-dim); }
.tpd-actions { display:flex; gap:10px; margin-top:10px; }

/* Sections */
.tpd-section { margin-block: 20px 20px 20px 50px; padding-block: 20px 20px 20px 20px; }
.tpd-section h3 { margin: 0 0 14px; font-weight: 700; letter-spacing: 0.3px; color: #dff3ff; }

/* Charts & Map */
.tpd-chart { height: clamp(260px, 36vw, 380px); padding: 12px; }
.tpd-map {
  width: 100%;
  height: clamp(320px, 42vw, 460px);
  border-radius: 12px;
  overflow: hidden;
}
.dim { opacity: 0.8; }



/* Errors */
.tpd-error { color: #ffb4c0; margin-top: 10px; text-shadow: 0 0 12px rgba(255,82,111,0.35); }

/* KPIs */
.kpi-grid { display:grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap:12px; }
.kpi { display:flex; flex-direction:column; align-items:flex-start; gap:6px; padding:16px; background:linear-gradient(180deg, rgba(18,29,48,0.8), rgba(11,18,32,0.8)); border:1px solid rgba(255,255,255,0.06); border-radius:14px; }
.kpi-icon { font-size:18px; opacity:0.9; }
.kpi-num { font-size:26px; font-weight:700; letter-spacing:.2px; }
.kpi-label { font-size:12px; opacity:.75; text-transform:uppercase; letter-spacing:.6px; }
/* skeleton shimmer */
.kpi.skeleton { position:relative; overflow:hidden; min-height:88px; }
.kpi.skeleton::after {
  content:""; position:absolute; inset:0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.08), transparent);
  animation: kpiShimmer 1.2s infinite;
}
@keyframes kpiShimmer { from { transform: translateX(-100%);} to { transform: translateX(100%);} }

/* Links */
.link { background:none; border:none; color:#4f46e5; cursor:pointer; padding:0; font:inherit; text-decoration: underline; }

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* Responsive tweaks */
@media (max-width: 1024px) {
  .tpd-chart { height: clamp(240px, 40vw, 340px); }
}
@media (max-width: 720px) {
  .tpd-artist { grid-template-columns: 1fr; text-align: center; }
  .tpd-artist-img { margin: 0 auto; }
  .tpd-actions { justify-content: center; }
}

/* Reduced motion: tone down heavy animations */
@media (prefers-reduced-motion: reduce) {
  .phase-60, .phase-80, .phase-100,
  .kpi.skeleton::after { animation: none !important; }
  .tpd-progress-bar { transition: width 150ms linear; }
}
</style>

<style scoped>
/* ===== Futuristic AI Dashboard Theme ===== */
:root { /* works with scoped by scoping to component root */ }

.tpd-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 1920px;
  padding: 60px 60px 60px 60px;
  min-height: 100vh;
  /* Layered animated gradients */
  background:
    radial-gradient(1200px 600px at 0% -10%, rgba(0, 255, 234, 0.12), transparent 60%),
    radial-gradient(900px 500px at 100% 110%, rgba(90, 110, 255, 0.10), transparent 60%),
    linear-gradient(135deg, #0b1220 0%, #0a0f1a 35%, #0b0f18 100%);
  color: #e8f4ff;
}


/* Subtle floating grid overlay */
.tpd-wrapper::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 24px 24px, 24px 24px;
  mask-image: radial-gradient(1200px 700px at 50% 50%, black 40%, transparent 100%);
  pointer-events: none;
}

/* Utility: glassy-neon card look */
.neo-card {
  background: linear-gradient(180deg, rgba(8, 3, 17, 0.08), rgba(255, 255, 255, 0.03));
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 16px;
  padding: 40px 40px 40px 40px;
  color: #d8ecff;
  box-shadow:
    0 0 0 1px rgba(0, 255, 230, 0.10) inset,
    0 10px 30px rgba(0, 0, 0, 0.30),
    0 0 40px rgba(56, 189, 248, 0.10);
}

/* Header / Controls */
.tpd-header { display: grid; gap: 16px; margin-bottom: 28px; }
.tpd-controls {
  display: flex;
  gap: 10px;
  align-items: center;
}



.tpd-overlay-thumb {
  width: 72px;
  height: 72px;
  border-radius: 12px;
  object-fit: cover;
  margin: 8px 0 4px 0;
  box-shadow: 0 6px 18px rgba(0,0,0,0.25);
}
.tpd-input {
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(10, 16, 28, 0.55);
  color: #e8f4ff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0,0,0,0.15) inset, 0 6px 16px rgba(0,0,0,0.2);
}
.tpd-input::placeholder { color: #96a6bf; }

.tpd-btn {
  align-self: stretch;
  padding: 12px 18px;
  border-radius: 12px;
  border: 0;
  background: linear-gradient(135deg, #00ffd1 0%, #4f46e5 100%);
  color: #041421;
  font-weight: 700;
  letter-spacing: 0.2px;
  cursor: pointer;
  box-shadow: 0 8px 28px rgba(79,70,229,0.25), 0 0 0 1px rgba(0,255,225,0.35) inset;
  transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;
}
.tpd-btn:hover { transform: translateY(-1px); filter: brightness(1.05); box-shadow: 0 12px 36px rgba(79,70,229,0.35), 0 0 0 1px rgba(0,255,225,0.55) inset; }
.tpd-btn:active { transform: translateY(0); }

.tpd-btn-ghost {
  background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03));
  color: #bbdefb;
  border: 1px solid rgba(255,255,255,0.16);
}

.tpd-chip {
  margin: 0 8px 0 0; padding: 8px 12px; border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.15);
  background: rgba(12, 20, 34, 0.45);
  color: #d8ecff;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.15s ease;
}
.tpd-chip:hover { box-shadow: 0 0 0 2px rgba(0,255,225,0.35) inset, 0 4px 16px rgba(0,0,0,0.3); transform: translateY(-1px); }

.tpd-spotlight { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; color: #97b3d5; }

/* Overlay */
.tpd-overlay { position: fixed; inset: 0; background: rgba(6, 10, 18, 0.65); display: grid; place-items: center; z-index: 999; }
.tpd-overlay-card { width: min(640px, 94vw); padding: 22px 22px 26px; border-radius: 18px; }
.tpd-overlay-card { composes: neo-card; }

/* Steps */
.tpd-steps { list-style: none; padding: 0; margin: 12px 0 18px; }
.tpd-step { display: grid; grid-template-columns: 28px 1fr; gap: 10px; align-items: center; padding: 8px 0; }
.tpd-step .tpd-step-index { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 8px; background: rgba(255,255,255,0.08); box-shadow: 0 0 0 1px rgba(255,255,255,0.15) inset; }
.tpd-step.in\ progress .tpd-step-index, .tpd-step.in.progress .tpd-step-index { background: rgba(253, 230, 138, 0.2); box-shadow: 0 0 0 1px rgba(253, 230, 138, 0.45) inset; }
.tpd-step.done .tpd-step-index { background: rgba(134, 239, 172, 0.2); box-shadow: 0 0 0 1px rgba(134, 239, 172, 0.45) inset; }
.tpd-status { margin-top: 10px; color: #1a6dc675; opacity: 0.4; }

.tpd-progress { width: 100%; height: 10px; background: rgba(255,255,255,0.08); border-radius: 999px; overflow: hidden; box-shadow: 0 0 0 1px rgba(255,255,255,0.12) inset; }
.tpd-progress-bar { height: 100%; background: linear-gradient(90deg, #00ffd1, #4f46e5, #06b6d4); filter: drop-shadow(0 0 10px rgba(0,255,209,0.5)); animation: sheen 2s linear infinite; }
@keyframes sheen { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }

/* Artist summary */
.tpd-artist { display: grid; grid-template-columns: 140px 1fr; gap: 18px; align-items: center; margin: 22px 0; padding: 16px; }
.tpd-artist { composes: neo-card; }
.tpd-artist-img { width: 140px; height: 140px; object-fit: cover; border-radius: 14px; box-shadow: 0 0 0 2px rgba(255,255,255,0.15) inset, 0 12px 28px rgba(0,0,0,0.35); }
.tpd-artist-meta h2 { margin: 0 0 6px; letter-spacing: 0.3px; }
.tpd-pattern { color: #b7d2ff; }
.tpd-actions { display: flex; gap: 10px; margin-top: 10px; }
.tpd-overlay-card {
  background: linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.03));
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255,255,255,.16);
  border-radius: 16px;
  box-shadow:
    0 0 0 1px rgba(0,255,230,.10) inset,
    0 10px 30px rgba(0,0,0,.30),
    0 0 40px rgba(56,189,248,.10);
}
/* Sections */
.tpd-section { margin: 28px 40px 28px 40px; }
.tpd-section h3 { margin: 0 0 14px; font-weight: 700; letter-spacing: 0.3px; color: #dff3ff; }

/* Chart & Map as glass cards */
.tpd-chart { height: 360px; padding: 12px; }

.tpd-map {
  width: 100%;
  height: 420px; /* adjust as needed */
  border-radius: 12px;
  overflow: hidden;
}
.dim { opacity: 0.8; }


/* Year popup */
.tpd-year-popup { margin-top: 14px; padding: 12px 14px; border-radius: 12px; }
.tpd-year-popup h4 { margin: 0 0 8px; color: #e6f3ff; }
.tpd-year-popup li { color: #cfe4ff; }

/* Saved forecasts */
.tpd-saved { display: grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 90px; }
.tpd-card { display: grid; grid-template-columns: 120px 1fr auto; align-items: center; gap: 40px; padding: 14px; }
.tpd-card img { width: 120px; height: 120px; object-fit: cover; border-radius: 10px; box-shadow: 0 0 0 1px rgba(255,255,255,0.12) inset; }
.tpd-card-actions { display: flex; gap: 8px; }

/* Errors */
.tpd-error { color: #ffb4c0; margin-top: 10px; text-shadow: 0 0 12px rgba(255, 82, 111, 0.35); }

/* Responsive */
@media (max-width: 1024px) {
  .tpd-wrapper { padding: 24px 16px 48px; }
  .tpd-chart { height: 320px; }
  .tpd-map { height: 420px; }
}
@media (max-width: 720px) {
  .tpd-controls { grid-template-columns: 1fr; }
  .tpd-artist { grid-template-columns: 1fr; text-align: center; }
  .tpd-artist-img { margin: 0 auto; }
  .tpd-actions { justify-content: center; }
  .tpd-chart { height: 280px; }
  .tpd-map { height: 360px; }
}
.link {
  background: none;
  border: none;
  color: #4f46e5;
  cursor: pointer;
  padding: 0;
  font: inherit;
  text-decoration: underline;
}
.kpi { display:flex; flex-direction:column; align-items:flex-start; padding:16px; }
.kpi-num { font-size:28px; font-weight:800; line-height:1; }
.kpi-label { opacity:0.75; margin-top:4px; font-size:12px; letter-spacing:.02em; }
.chip { display:inline-block; padding:4px 8px; border-radius:999px; background:#0e223a; border:1px solid rgba(255,255,255,.08); font-size:12px; }
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.dim { font-size: 16px;}
.kpi-grid {
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(180px,1fr));
  gap:12px;
}

.kpi {
  padding:16px;
  display:flex;
  flex-direction:column;
  align-items:flex-start;
  gap:6px;
  background:linear-gradient(180deg, rgba(18,29,48,0.8), rgba(11,18,32,0.8));
  border:1px solid rgba(255,255,255,0.06);
  border-radius:14px;
}

.kpi-icon { font-size:18px; opacity:0.9; }
.kpi-num  { font-size:26px; font-weight:700; letter-spacing:.2px; }
.kpi-label{ font-size:12px; opacity:.75; text-transform:uppercase; letter-spacing:.6px; }

/* skeleton shimmer */
.kpi.skeleton {
  position:relative; overflow:hidden; min-height:88px;
}
.kpi.skeleton::after {
  content:""; position:absolute; inset:0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.08), transparent);
  animation: kpiShimmer 1.2s infinite;
}
@keyframes kpiShimmer { from { transform: translateX(-100%);} to { transform: translateX(100%);} }
/* Layout */
.tpd-socials {
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;
}

.tpd-social-item {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  margin-bottom: 8px;
}

.tpd-social-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: block;
}

.tpd-social-meta {
  min-width: 0;
}

.tpd-social-name {
  display: block;
  line-height: 1.1;
}

/* Button with subtle animated glow on hover */
.tpd-social-btn {
  padding: 6px 12px;
  border-radius: 10px;
  font-weight: 600;
  transform: translateZ(0);
  transition: transform 200ms ease, box-shadow 300ms ease, background 300ms ease;
}

.tpd-social-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 0 0 0 rgba(255, 51, 68, 0.0);
  animation: tpd-glow 1.6s ease-in-out infinite;
}

/* List pop-in animation */
.pop-enter-active, .pop-leave-active {
  transition: all 250ms cubic-bezier(.2,.8,.2,1);
}
.pop-enter, .pop-leave-to {
  opacity: 0;
  transform: translateY(6px) scale(0.98);
}

/* Glow keyframes */
@keyframes tpd-glow {
  0%   { box-shadow: 0 0 0 0 rgba(255, 51, 68, 0.0); }
  50%  { box-shadow: 0 0 18px 2px rgba(255, 51, 68, 0.35); }
  100% { box-shadow: 0 0 0 0 rgba(255, 51, 68, 0.0); }
}
.tpd-gamify-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}
.neo-card.hud { padding: 14px; }
.hud-title { font-size: 12px; text-transform: uppercase; letter-spacing: .08em; color: #ffb3b3; }
.hud-value { font-size: 28px; font-weight: 800; line-height: 1; margin-top: 6px; color: #fff; }
.hud-sub   { font-size: 12px; opacity: .8; margin-top: 4px; }

.hud-bar { position: relative; height: 6px; border-radius: 999px; background: rgba(255,255,255,0.08); margin-top: 10px; overflow: hidden; }
.hud-bar > span { position: absolute; inset: 0 0 0 0; width: 0%; background: linear-gradient(90deg, #ef4444, #f59e0b); transition: width 400ms ease; }

.neo-card.hud.pulse { animation: hudPulse 0.7s ease; }
@keyframes hudPulse { 0% { transform: scale(1); } 35% { transform: scale(1.02); } 100% { transform: scale(1); } }

/* Reveal animation for staged sections */
.reveal-enter-active, .reveal-leave-active { transition: all .45s cubic-bezier(.2,.8,.2,1); }
.reveal-enter-from, .reveal-leave-to { opacity: 0; transform: translateY(10px) scale(.98); }

/* Buttons: subtle red accent */
.tpd-btn { transition: transform .15s ease, box-shadow .25s ease; }
.tpd-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 22px rgba(239,68,68,.25); }
.tpd-gamify-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}
.neo-card.hud { padding: 14px; }
.hud-title { font-size: 12px; text-transform: uppercase; letter-spacing: .08em; color: #ffb3b3; }
.hud-value { font-size: 28px; font-weight: 800; line-height: 1; margin-top: 6px; color: #fff; }
.hud-sub   { font-size: 12px; opacity: .8; margin-top: 4px; }

.hud-bar { position: relative; height: 6px; border-radius: 999px; background: rgba(255,255,255,0.08); margin-top: 10px; overflow: hidden; }
.hud-bar > span { position: absolute; inset: 0 0 0 0; width: 0%; background: linear-gradient(90deg, #ef4444, #f59e0b); transition: width 400ms ease; }

.neo-card.hud.pulse { animation: hudPulse 0.7s ease; }
@keyframes hudPulse { 0% { transform: scale(1); } 35% { transform: scale(1.02); } 100% { transform: scale(1); } }

/* Reveal animation for staged sections */
.reveal-enter-active, .reveal-leave-active { transition: all .45s cubic-bezier(.2,.8,.2,1); }
.reveal-enter-from, .reveal-leave-to { opacity: 0; transform: translateY(10px) scale(.98); }

/* Buttons: subtle red accent */
.tpd-btn { transition: transform .15s ease, box-shadow .25s ease; }
.tpd-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 22px rgba(239,68,68,.25); }
.hud-pill {
  display:inline-flex; align-items:baseline; gap:6px;
  background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.08);
  padding:6px 10px; border-radius:12px;
}
.hud-label { font-size:12px; color:#a9c4ff; text-transform:uppercase; letter-spacing:.04em; }
.hud-value { font-size:18px; font-weight:700; color:#fff; }

.badge-chip {
  display:inline-flex; align-items:center; gap:6px;
  padding:6px 10px; border-radius:999px;
  background:linear-gradient(135deg, rgba(239,68,68,.15), rgba(239,68,68,.05));
  border:1px solid rgba(239,68,68,.3);
  color:#ffdedd; font-weight:600; box-shadow:0 1px 6px rgba(0,0,0,.25);
}

.hud-feed { list-style:none; margin:0; padding:0; }
.feed-item {
  display:grid; grid-template-columns:auto 1fr auto; gap:10px;
  padding:8px 0; border-bottom:1px dashed rgba(255,255,255,.08);
}
.feed-icon { width:20px; text-align:center; }
.feed-text { color:#eaf2ff; }
.feed-time { font-size:12px; }
.pop-enter-active, .pop-leave-active { transition: all .28s ease; }
.pop-enter, .pop-leave-to { transform:scale(.9); opacity:0; }
.pop-in { animation: popIn .35s ease-out both; }
@keyframes popIn { from{ transform:scale(.9); opacity:0 } to{ transform:scale(1); opacity:1 } }
/* reveal enter (already have name="reveal") */
.reveal-enter-active { animation: cardIn 480ms cubic-bezier(.2,.9,.25,1) both; }
@keyframes cardIn {
  from { opacity: 0; transform: translateY(14px) scale(.98); filter: blur(2px); }
  to   { opacity: 1; transform: translateY(0) scale(1);      filter: blur(0);  }
}

/* “filling” shimmer overlay */
.card.is-filling {
  position: relative;
  overflow: hidden;
}
.card.is-filling::after {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(100deg, rgba(255,255,255,0) 10%, rgba(255,255,255,.08) 40%, rgba(255,255,255,0) 70%)
    , rgba(0,0,0,0);
  transform: translateX(-100%);
  animation: fillSweep 900ms ease-out forwards;
  pointer-events: none;
}
@keyframes fillSweep {
  to { transform: translateX(100%); }
}

/* completed pulse + glow */
.card.is-complete {
  animation: completePop 320ms ease-out;
  box-shadow: 0 0 0 0 rgba(142, 240, 180, 0.0);
}
@keyframes completePop {
  0%   { transform: scale(1); }
  40%  { transform: scale(1.01); box-shadow: 0 0 0 6px rgba(142, 240, 180, .18); }
  100% { transform: scale(1);   box-shadow: 0 0 0 0 rgba(142, 240, 180, 0.0); }
}

/* HUD pulse */
.hud.pulse, .hud.is-pulse, .hud[data-pulse="1"], .hud-pill.pop-in {
  animation: hudPulse 650ms ease-out;
}
@keyframes hudPulse {
  0%   { transform: scale(1); }
  45%  { transform: scale(1.02); }
  100% { transform: scale(1); }
}

/* XP sparkle on the score */
.hud-value { position: relative; display: inline-block; }
.hud-value::after {
  content: attr(data-xp);
  position: absolute;
  left: 50%; transform: translateX(-50%);
  top: -1.2em;
  font-size: 10px; opacity: 0; pointer-events: none;
}
.xp-burst .hud-value::after {
  content: '+';
  animation: xpRise 220ms ease-out;
}
@keyframes xpRise {
  from { transform: translate(-50%, 8px); opacity: 0; }
  to   { transform: translate(-50%, -2px); opacity: 1; }
}

</style>
